import React, { Component } from 'react';
import { Row, Col, Modal, Form } from 'react-bootstrap';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import * as Cookies from 'es-cookie';
import {
  getAuthorizedBranchIds,
  getAuthorizedSectionIds,
  getAuthorizedClassIds,
  getAuthorizedSubjectIds,
  filterByAuthorizedIds,
} from '../../utils/authHelper';
import { attachModuleGuard, AccessDeniedModal } from '../../../utils/moduleGuard';
import CheckboxDropdown from '../ui/CheckboxDropdown';
import './_timetable.scss';

// This page is the "timetable_builder" module. Its grant row uses sub_module_name
// "Time Table", so scope every check to it. module_key ONLY (never module_name).
const TIMETABLE_MODULE = ['timetable_builder'];
const TIMETABLE_SUB = 'Time Table';

// ─── GraphQL operations ───────────────────────────────────────────────────────
const UPSERT_TIMETABLE = gql`
  mutation($params: UpsertTimetableInput) {
    upsertTimetable(params: $params) { id }
  }
`;
const GET_TIMETABLE_PERIODS = gql`
  query($institute_id: Int!, $timetable_id: Int!) {
    getTimetablePeriods(institute_id: $institute_id, timetable_id: $timetable_id) {
      id day_of_week slot_id period_type subject_id teacher_id room note
    }
  }
`;
const GET_TIMETABLE_SLOTS = gql`
  query getTimetableSlots($institute_id: Int!, $branch_id: Int!, $timetable_id: Int) {
    getTimetableSlots(institute_id: $institute_id, branch_id: $branch_id, timetable_id: $timetable_id) {
      id
      slot_key
      label
      start_time
      end_time
      slot_order
      slot_type
      estatus
    }
  }
`;
const SAVE_TIMETABLE_PERIODS = gql`
  mutation($params: SaveTimetablePeriodsInput) {
    saveTimetablePeriods(params: $params)
  }
`;
const GET_HOLIDAYS = gql`
  query($institute_id: Int!, $branch_name: String, $month: Int, $year: Int) {
    getTimetableHolidays(institute_id: $institute_id, branch_name: $branch_name, month: $month, year: $year) {
      id holiday_date name reason type
    }
  }
`;
const SAVE_HOLIDAY = gql`
  mutation($params: SaveHolidayInput) {
    saveHoliday(params: $params) { id holiday_date name reason type }
  }
`;
const DELETE_HOLIDAY = gql`
  mutation($id: Int!, $institute_id: Int!) {
    deleteHoliday(id: $id, institute_id: $institute_id)
  }
`;
const GET_TEMPLATES = gql`
  query($institute_id: Int!) {
    getTimetableTemplates(institute_id: $institute_id) {
      id name context_hint client_uid weeks_data
    }
  }
`;
const SAVE_TEMPLATE = gql`
  mutation($params: SaveTemplateInput) {
    saveTemplate(params: $params) { id client_uid }
  }
`;
const DELETE_TEMPLATE = gql`
  mutation($id: Int!, $institute_id: Int!) {
    deleteTemplate(id: $id, institute_id: $institute_id)
  }
`;
const SAVE_TIMETABLE_CONFIG = gql`
  mutation($params: SaveTimetableConfigInput) {
    saveTimetableConfig(params: $params) { id bell_schedule }
  }
`;
const SAVE_TIMETABLE_SLOTS = gql`
  mutation saveTimetableSlots($institute_id: Int!, $branch_id: Int!, $timetable_id: Int, $class_id: Int, $slots: [TimetableSlotInput]!) {
    saveTimetableSlots(institute_id: $institute_id, branch_id: $branch_id, timetable_id: $timetable_id, class_id: $class_id, slots: $slots)
  }
`;
const SAVE_TIMETABLE_SUBJECTS = gql`
  mutation($timetable_id: Int!, $institute_id: Int!, $subjects: JSON!) {
    saveTimetableSubjects(timetable_id: $timetable_id, institute_id: $institute_id, subjects: $subjects)
  }
`;
const SAVE_TIMETABLE_SUBSTITUTION = gql`
  mutation saveTimetableSubstitution($params: SaveTimetableSubstitutionInput!) {
    saveTimetableSubstitution(params: $params)
  }
`;
const GET_SUBSTITUTIONS = gql`
  query getTimetableSubstitutions($institute_id: Int!, $timetable_id: Int!, $substitution_date: String) {
    getTimetableSubstitutions(institute_id: $institute_id, timetable_id: $timetable_id, substitution_date: $substitution_date) {
      id institute_id timetable_id substitution_date day_of_week slot_id original_teacher_id substitute_id subject_id reason status
    }
  }
`;
const GET_TIMETABLE_CONFIG = gql`
  query($institute_id: Int!, $branch_id: Int!, $academic_year_id: Int, $term: Int) {
    getTimetableConfig(institute_id: $institute_id, branch_id: $branch_id, academic_year_id: $academic_year_id, term: $term) {
      id bell_schedule start_date end_date working_days
    }
  }
`;

// ─── Bell schedule — 8 × 45 min (Indian private school) ──────────────────────
const BELL_SCHEDULE = [
  { slot: 'assembly', label: 'Assembly & Prayer', start: '08:00', end: '08:20', type: 'special' },
  { slot: 'p1',  label: 'Period 1', start: '08:20', end: '09:05', type: 'period', num: 1 },
  { slot: 'p2',  label: 'Period 2', start: '09:05', end: '09:50', type: 'period', num: 2 },
  { slot: 'p3',  label: 'Period 3', start: '09:50', end: '10:35', type: 'period', num: 3 },
  { slot: 'recess', label: 'Recess', start: '10:35', end: '10:50', type: 'break' },
  { slot: 'p4',  label: 'Period 4', start: '10:50', end: '11:35', type: 'period', num: 4 },
  { slot: 'p5',  label: 'Period 5', start: '11:35', end: '12:20', type: 'period', num: 5 },
  { slot: 'lunch', label: 'Lunch Break', start: '12:20', end: '13:05', type: 'break' },
  { slot: 'p6',  label: 'Period 6', start: '13:05', end: '13:50', type: 'period', num: 6 },
  { slot: 'p7',  label: 'Period 7', start: '13:50', end: '14:35', type: 'period', num: 7 },
  { slot: 'p8',  label: 'Period 8', start: '14:35', end: '15:20', type: 'period', num: 8 },
];
let PERIOD_SLOTS = BELL_SCHEDULE.filter(s => s.type === 'period').map(s => s.slot);

// ─── GQL — branches/classes (aligned with StudentAttendance) + employees ─────
const FETCH_BRANCHES = gql`
  query getBranches($institution_id: Int!) {
    getBranches(institution_id: $institution_id) {
      id
      branch_name
      sections {
        id
        class_id
        section_name
      }
    }
  }
`;

const GET_CLASSES = gql`
  query($id: Int) {
    getExamClasses(id: $id) {
      id
      class
    }
  }
`;
const GET_EMPLOYEES = gql`
  query($institute_id: Int, $branch_id: Int) {
    getEmployees(institute_id: $institute_id, branch_id: $branch_id) {
      employee_id employee_name subject_ids employee_type_id
    }
  }
`;

const GET_ACADEMIC_YEARS = gql`
  query getAcademicYears($id: Int) {
    getAcademicYears(id: $id) {
      id
      academic_year
    }
  }
`;

const GET_CLASS_SUBJECTS = gql`
  query getClassSubjectsByAcademicYear($institute_id: Int!, $classIds: String, $academic_year_id: Int!) {
    getClassSubjectsByAcademicYear(institute_id: $institute_id, classIds: $classIds, academic_year_id: $academic_year_id) {
      id
      class_id
      subject_ids
      subject_names
      checked
    }
  }
`;

// ─── Subjects with colors ─────────────────────────────────────────────────────
// Default subject palette — overridden per class/section from DB
const DEFAULT_SUBJECTS = [
  { id: 1,  name: 'Mathematics',        short: 'Maths',    code: 'MAT-101', color: '#2563eb' },
  { id: 2,  name: 'Physics',            short: 'Physics',  code: 'PHY-101', color: '#7c3aed' },
  { id: 3,  name: 'Chemistry',          short: 'Chem',     code: 'CHM-101', color: '#0891b2' },
  { id: 4,  name: 'Biology',            short: 'Bio',      code: 'BIO-101', color: '#16a34a' },
  { id: 5,  name: 'English',            short: 'English',  code: 'ENG-101', color: '#1a8a7a' },
  { id: 6,  name: 'Hindi',              short: 'Hindi',    code: 'HIN-101', color: '#dc2626' },
  { id: 7,  name: 'Social Science',     short: 'Soc.Sci',  code: 'SST-101', color: '#d97706' },
  { id: 8,  name: 'Computer Science',   short: 'CS',       code: 'CS-201',  color: '#6366f1' },
  { id: 9,  name: 'Physical Education', short: 'P.T.',     code: 'PE-101',  color: '#65a30d' },
  { id: 10, name: 'Art & Drawing',      short: 'Art',      code: 'ART-101', color: '#ec4899' },
];

const SUBJECT_COLORS = [
  '#2563eb','#7c3aed','#0891b2','#16a34a','#dc2626','#d97706',
  '#6366f1','#0d9488','#ec4899','#f97316','#92400e','#15803d',
  '#8b5cf6','#65a30d','#b45309','#3b82f6','#a855f7','#64748b',
];

// Teachers loaded from API at runtime (see loadBranchData)

const ROOMS = ['Own Classroom', 'Physics Lab', 'Chemistry Lab', 'Biology Lab', 'Computer Lab', 'PT Ground', 'Music Room', 'Art Room', 'Library', 'Assembly Hall', 'Seminar Hall'];
const DAYS       = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_SHORT = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ─── Holiday types ────────────────────────────────────────────────────────────
const HOLIDAY_TYPES = [
  { key: 'national', label: 'National',  icon: 'fal fa-flag',        color: '#dc2626', bg: '#fbeeea', border: '#f3d2c8' },
  { key: 'state',    label: 'State',     icon: 'fal fa-map',         color: '#d97706', bg: '#fdf3e0', border: '#e8d5a0' },
  { key: 'school',   label: 'School',    icon: 'fal fa-school',      color: '#7c4dcc', bg: '#f0ebfb', border: '#d5c7f0' },
  { key: 'local',    label: 'Local',     icon: 'fal fa-city',        color: '#3d8a55', bg: '#e8f3ec', border: '#b8ddc8' },
  { key: 'exam_day', label: 'Exam Day',  icon: 'fal fa-pen-nib',     color: '#f57c00', bg: '#fff3e0', border: '#ffe0b2' },
];

// ─── Date utilities ───────────────────────────────────────────────────────────
function getMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
function fmtDate(date) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
function fmtShort(date)   { return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
function fmtFull(date)    { return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }); }
function fmtWeekRange(monday) {
  const sat = addDays(monday, 5);
  const sameMonth = monday.getMonth() === sat.getMonth();
  return sameMonth
    ? `${monday.getDate()} – ${sat.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`
    : `${fmtShort(monday)} – ${sat.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function uid()          { return `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`; }
let _activeSubjects = DEFAULT_SUBJECTS;
function getSubject(id) { return _activeSubjects.find(s => s.id === parseInt(id)) || null; }
function makeInitials(name) {
  return (name || '').split(' ').filter(w => w).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}
function emptyDay()     { const d = {}; PERIOD_SLOTS.forEach(s => { d[s] = null; }); return d; }
function emptyWeek()    { const w = {}; [1,2,3,4,5,6].forEach(d => { w[d] = emptyDay(); }); return w; }
function filledCount(day) { return PERIOD_SLOTS.filter(s => day?.[s]).length; }
function weekTotal(week)  { let n = 0; [1,2,3,4,5,6].forEach(d => { n += filledCount(week[d]); }); return n; }
function deepCloneWeek(week) {
  const out = {};
  [1,2,3,4,5,6].forEach(d => {
    out[d] = {};
    PERIOD_SLOTS.forEach(s => { out[d][s] = week[d]?.[s] ? { ...week[d][s] } : null; });
  });
  return out;
}

// Returns 1 (Mon) … 6 (Sat); Sunday maps to 1
function getTodayDayNum() {
  const d = new Date().getDay();
  return d === 0 ? 1 : d;
}

// Count how many of each weekday (1-6) fall in [fromStr, toStr] (YYYY-MM-DD)
function weekdayCounts(fromStr, toStr) {
  const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
  if (!fromStr || !toStr) return counts;
  const cur = new Date(fromStr);
  const end = new Date(toStr);
  if (cur > end) return counts;
  while (cur <= end) {
    const d = cur.getDay();
    if (d >= 1 && d <= 6) counts[d]++;
    cur.setDate(cur.getDate() + 1);
  }
  return counts;
}

const PERIOD_TYPES = [
  { key: 'class', icon: 'fal fa-book',        label: 'Class',  sub: 'Lecture period' },
  { key: 'lab',   icon: 'fal fa-flask',       label: 'Lab',    sub: 'Lab session'    },
  { key: 'exam',  icon: 'fal fa-pen-nib',     label: 'Exam',   sub: 'Exam / Test'    },
  { key: 'break', icon: 'fal fa-coffee',      label: 'Break',  sub: 'Short / Lunch'  },
  { key: 'free',  icon: 'fal fa-leaf',        label: 'Free',   sub: 'Study / Mentor' },
];

const EMPTY_FORM = { type: 'class', subjectId: '', teacherId: '', room: 'Own Classroom', note: '', startTime: '', endTime: '', subjectIds: [], teacherIds: [] };

// ─── Component ────────────────────────────────────────────────────────────────
class TimetableBuilder extends Component {
  constructor(props) {
    super(props);

    this.state = {
      // ── Selection ──
      selectedBranch:   '',
      selectedBranchId: null,
      selectedClass:    '',
      selectedClassId:  null,
      selectedSection:  '',
      selectedSectionId: null,
      activeDay:        getTodayDayNum(),

      // ── API-loaded master data ──
      branches:    [],   // [{ id, name }]
      branchesWithSections: [], // getBranches — nested sections per branch
      examClasses: [],   // getExamClasses — id / class labels
      classesList: [],   // [{ id, label, sections: ['A','B',...] }]
      teachers:    [],   // [{ id, name, initials, subject }]
      sectionIdMap: {},  // { 'classId||sectionName': sectionId }

      // ── Timetable data ──
      timetable: {},
      templates: [],

      viewMode: 'student',

      // ── Week navigation ──
      weekStart:          getMonday(new Date()),
      calPickerMonth:     new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      showCalendarPicker: false,

      // ── Holidays ──
      holidays:         {},
      showHolidayModal: false,
      holidayTargetDay: null,
      holidayForm:      { name: '', reason: '', type: 'national' },

      // ── Clash detection ──
      clashWarning:  null,
      pendingAssign: null,

      // ── Bell schedule ──
      customBellSchedule: null,
      showBellConfigModal: false,
      bellConfigDraft:     null,
      slotsList:           [],

      // ── Assign modal ──
      showAssignModal: false,
      assignSlot:      null,
      assignForm:      { ...EMPTY_FORM },

      // ── Copy modal ──
      showCopyModal:     false,
      copyMode:          'day',
      copyTargetDays:    [],
      copyTargetBranch:  '',
      copyTargetClass:   '',
      copyDateFrom:      '',
      copyDateTo:        '',
      configDbId:        null,
      copyTargetSection: '',

      // ── Template modals ──
      showSaveTemplateModal: false,
      templateNameInput:     '',
      showLoadTemplateModal: false,

      saveFlash: false,

      // ── API sync ──
      timetableDbId: null,
      holidayDbIds:  {},
      apiReady:      false,

      // ── Subjects (dynamic per class/section) ──
      subjects:           [...DEFAULT_SUBJECTS],
      showSubjectMgr:     false,
      newSubjectName:     '',
      newSubjectShort:    '',
      newSubjectCode:     '',
      newSubjectColor:    SUBJECT_COLORS[0],
      // ── Teacher Substitution modal state ──
      showSubstituteModal:         false,
      substituteTargetSlot:        null,
      selectedSubstituteTeacherId: '',
      substituteReason:            '',
      substituteLoading:           false,
      substituteError:             '',
      substituteMap:               {},

      // Flipped to true by the guard when a role without is_edit triggers a write.
      accessDeniedShow:   false,
    };

    // Scoped to the "Time Table" sub-module. Every write entry point runs the guard; it
    // opens the Access Denied modal and stops when the role lacks is_edit. Reads (loading
    // the timetable, navigating weeks, viewing, printing) are never blocked.
    this.guard = attachModuleGuard(this, TIMETABLE_MODULE, TIMETABLE_SUB);
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────
  componentDidMount() {
    this.loadMeta();
  }

  componentDidUpdate(prevProps, prevState) {
    const { selectedBranch, selectedBranchId, selectedClass, selectedSection, weekStart } = this.state;

    // Branch switched → reload employees + sections, then timetable
    if (prevState.selectedBranch !== selectedBranch && selectedBranchId) {
      this.loadBranchData(selectedBranchId).then(() => this.loadFromApi());
      return;
    }
    // Class or section changed → reload timetable only
    if (prevState.selectedClass !== selectedClass || prevState.selectedSection !== selectedSection) {
      this.loadFromApi();
      return;
    }
    // Week navigated into a different month → reload holidays for that month
    if (
      prevState.weekStart.getMonth()     !== weekStart.getMonth() ||
      prevState.weekStart.getFullYear()  !== weekStart.getFullYear()
    ) {
      this.reloadHolidays();
    }
  }

  // ─── API: branches (with sections) + class labels once; then per-branch teachers ──────────────
  loadMeta = async () => {
    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!instituteId || !this.props.client) return;
    try {
      const [branchRes, classRes] = await Promise.all([
        this.props.client.query({
          query: FETCH_BRANCHES,
          variables: { institution_id: instituteId },
          fetchPolicy: 'network-only',
        }),
        this.props.client.query({
          query: GET_CLASSES,
          variables: { id: 1 },
          fetchPolicy: 'network-only',
        }),
      ]);
      let branchesRaw = branchRes.data?.getBranches || [];
      // Show only the branches/sections/classes the user is authorized for. The *_id
      // cookies are comma-separated lists (e.g. "10,12,15"), so match against every
      // id. An empty cookie means that dimension was never assigned, so it stays
      // unrestricted (see authHelper.js).
      const authorizedIds = getAuthorizedBranchIds();
      if (authorizedIds.length) {
        branchesRaw = branchesRaw.filter(b => authorizedIds.includes(String(b.id)));
      }

      const authorizedSectionIds = getAuthorizedSectionIds();
      const authorizedClassIds = getAuthorizedClassIds();
      if (authorizedSectionIds.length || authorizedClassIds.length) {
        branchesRaw = branchesRaw.map(b => {
          let sections = b.sections || [];
          if (authorizedSectionIds.length) {
            sections = filterByAuthorizedIds(sections, authorizedSectionIds, 'id');
          }
          if (authorizedClassIds.length) {
            sections = filterByAuthorizedIds(sections, authorizedClassIds, 'class_id');
          }
          return { ...b, sections };
        });
      }
      const examClasses = classRes.data?.getExamClasses || [];
      const branches = branchesRaw.map(b => ({
        id: parseInt(b.id),
        name: b.branch_name,
      }));
      const stateUpdate = {
        branches,
        branchesWithSections: branchesRaw,
        examClasses,
      };
      let firstBranchId = null;
      if (branches.length > 0) {
        firstBranchId = branches[0].id;
        stateUpdate.selectedBranch = branches[0].name;
        stateUpdate.selectedBranchId = firstBranchId;
      }
      this.setState(stateUpdate);

      if (firstBranchId != null) {
        await this.loadBranchData(firstBranchId, branchesRaw, examClasses);
      }
    } catch (err) {
      console.warn('[TimetableBuilder] loadMeta failed:', err);
    }
  };

  // ─── Derive classes + sections from getBranches nested sections + getExamClasses labels ────────
  loadBranchData = async (branchId, branchesRawOverride = null, examClassesOverride = null) => {
    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!instituteId || !this.props.client) return;
    try {
      const branchesWS = branchesRawOverride || this.state.branchesWithSections;
      const examClassesList = examClassesOverride || this.state.examClasses || [];

      const classNameMap = {};
      examClassesList.forEach(c => {
        classNameMap[parseInt(c.id)] = c.class;
      });

      const branchObj = branchesWS.find(b => parseInt(b.id) === branchId);
      const sectRows = branchObj?.sections || [];

      const classMap = {};
      const sectionIdMap = {};
      sectRows.forEach(row => {
        const classId = parseInt(row.class_id);
        const sectionId = parseInt(row.id);
        const secName = (row.section_name != null ? String(row.section_name) : '') || String(sectionId);
        if (!classMap[classId]) {
          classMap[classId] = {
            id: String(classId),
            label: classNameMap[classId] || `Class ${classId}`,
            sections: [],
          };
        }
        if (!classMap[classId].sections.includes(secName)) {
          classMap[classId].sections.push(secName);
        }
        sectionIdMap[`${classId}||${secName}`] = sectionId;
      });
      const classesList = Object.values(classMap)
        .map(c => ({
          ...c,
          sections: [...c.sections].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
        }))
        .filter(c => c.sections.length > 0)
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

      // Employees — branch-specific
      const empRes = await this.props.client.query({
        query: GET_EMPLOYEES,
        variables: { institute_id: instituteId, branch_id: branchId },
        fetchPolicy: 'network-only',
      });
      const teachers = (empRes.data?.getEmployees || []).map(e => ({
        id:       e.employee_id,
        name:     e.employee_name,
        initials: makeInitials(e.employee_name),
        subject:  e.subject_ids || '',
      }));

      this.setState({ classesList, teachers, sectionIdMap });

      // Set initial class/section on first load
      if (classesList.length > 0 && !this.state.selectedClass) {
        const firstClass = classesList[0];
        const firstSec   = firstClass.sections[0] || '';
        const classId    = parseInt(firstClass.id);
        const sectionId  = sectionIdMap[`${classId}||${firstSec}`] || null;
        this.setState({
          selectedClass:     firstClass.label,
          selectedClassId:   classId,
          selectedSection:   firstSec,
          selectedSectionId: sectionId,
          copyTargetClass:   firstClass.label,
          copyTargetSection: firstClass.sections[1] || firstSec,
        }, () => this.loadFromApi());
      } else {
        this.loadFromApi();
      }
    } catch (err) {
      console.warn('[TimetableBuilder] loadBranchData failed:', err);
    }
  };

  // ─── API: load timetable + holidays + templates ───────────────────────────
  loadFromApi = async () => {
    const { selectedBranch, selectedBranchId, selectedClassId, selectedSectionId, weekStart } = this.state;
    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!instituteId || !this.props.client || !selectedBranchId || !selectedClassId || !selectedSectionId) return;

    // Reset timetableDbId to null so lookups never leak data from previous class/section selection
    this.setState({ timetableDbId: null });

    try {
      // 1. Get/create the timetable container row
      const ttRes = await this.props.client.mutate({
        mutation: UPSERT_TIMETABLE,
        variables: { params: { institute_id: instituteId, branch_id: selectedBranchId, class_id: selectedClassId, section_id: selectedSectionId } },
      });
      const ttRow       = ttRes.data?.upsertTimetable;
      const timetableDbId = ttRow?.id;
      if (!timetableDbId) return;

      // Fetch dynamic subjects mapped directly to the class
      let curriculumSubjects = [];
      try {
          const ayRes = await this.props.client.query({
              query: GET_ACADEMIC_YEARS,
              variables: { id: 1 },
              fetchPolicy: 'network-only'
          });
          const years = ayRes.data?.getAcademicYears || [];
          const currentYear = new Date().getFullYear();
          const month = new Date().getMonth();
          const targetAyStr = month >= 5 ? `${currentYear}-${currentYear + 1}` : `${currentYear - 1}-${currentYear}`;
          const matchedYear = years.find(y => y.academic_year.includes(targetAyStr)) || years[0];
          const resolvedAyId = matchedYear ? matchedYear.id : null;

          if (resolvedAyId) {
              const subRes = await this.props.client.query({
                  query: GET_CLASS_SUBJECTS,
                  variables: {
                      institute_id: instituteId,
                      classIds: String(selectedClassId),
                      academic_year_id: parseInt(resolvedAyId)
                  },
                  fetchPolicy: 'network-only'
              });
              const maps = subRes.data?.getClassSubjectsByAcademicYear || [];
              if (maps.length > 0) {
                  const subjectIds = maps[0].subject_ids.split(",").map(id => parseInt(id.trim())).filter(Boolean);
                  const subjectNames = maps[0].subject_names.split(",").map(n => n.trim()).filter(Boolean);
                  const checkedArray = maps[0].checked.split(",").map(chk => chk.trim() === "true");

                  subjectIds.forEach((sid, idx) => {
                      if (checkedArray[idx]) {
                          curriculumSubjects.push({
                              id: sid,
                              name: subjectNames[idx],
                              short: subjectNames[idx].slice(0, 6),
                              code: `SUB-${sid}`,
                              color: SUBJECT_COLORS[idx % SUBJECT_COLORS.length]
                          });
                      }
                  });
              }
          }
      } catch (e) {
          console.error("[TimetableBuilder] Failed to fetch curriculum subjects:", e);
      }

      if (curriculumSubjects.length === 0) {
          curriculumSubjects = [...DEFAULT_SUBJECTS];
      }

      // Restrict to the subjects this user is authorized for, same "empty cookie =
      // unrestricted" convention as branch/section/class above. Applied after the
      // DEFAULT_SUBJECTS fallback so an authorized-but-empty result isn't masked by
      // re-populating with every default subject.
      const authorizedSubjectIds = getAuthorizedSubjectIds();
      if (authorizedSubjectIds.length) {
          curriculumSubjects = filterByAuthorizedIds(curriculumSubjects, authorizedSubjectIds, 'id');
      }

      _activeSubjects = curriculumSubjects;
      this.setState({ timetableDbId, subjects: curriculumSubjects });

      // 2a. Load branch-level slots and config
      let mappedSlots = [];
      try {
        const slotsRes = await this.props.client.query({
          query: GET_TIMETABLE_SLOTS,
          variables: { institute_id: instituteId, branch_id: selectedBranchId, timetable_id: timetableDbId },
          fetchPolicy: 'network-only',
        });
        const slots = slotsRes.data?.getTimetableSlots || [];
        mappedSlots = slots.map(b => {
          const cleanKey = b.slot_key.replace(/^tt_\d+_/, '').replace(/^class_\d+_/, '');
          return {
            id: b.id,
            slot: cleanKey || b.slot_key,
            slot_key: cleanKey,
            label: b.label,
            start: b.start_time ? b.start_time.slice(0, 5) : '08:00',
            end: b.end_time ? b.end_time.slice(0, 5) : '08:45',
            type: b.slot_type,
            num: b.slot_order,
            slot_order: b.slot_order,
            estatus: b.estatus
          };
        });
        this.setState({ slotsList: mappedSlots });
        PERIOD_SLOTS = mappedSlots.filter(b => b.type === 'period').map(b => b.slot);

        const cfgRes = await this.props.client.query({
          query: GET_TIMETABLE_CONFIG,
          variables: { institute_id: instituteId, branch_id: selectedBranchId },
          fetchPolicy: 'network-only',
        });
        const cfg = cfgRes.data?.getTimetableConfig;
        if (cfg) {
          this.setState({
            configDbId: cfg.id,
            copyDateFrom: cfg.start_date || '',
            copyDateTo: cfg.end_date || '',
          });
        }
      } catch (err) {
        console.warn('[TimetableBuilder] Failed to load slots/config:', err);
      }

      // 2b. Load periods and convert to week state
      const perRes = await this.props.client.query({
        query: GET_TIMETABLE_PERIODS,
        variables: { institute_id: instituteId, timetable_id: timetableDbId },
        fetchPolicy: 'network-only',
      });
      const periods = perRes.data?.getTimetablePeriods || [];
      const loadedWeek = emptyWeek();
      periods.forEach(p => {
        if (p.subject_id && loadedWeek[p.day_of_week]) {
          const subjectIds = String(p.subject_id).split(',').map(id => parseInt(id)).filter(id => !isNaN(id));
          const teacherIds = p.teacher_id ? String(p.teacher_id).split(',').map(id => parseInt(id)).filter(id => !isNaN(id)) : [];
          const activeSlots = mappedSlots.length > 0 ? mappedSlots : (this.state.slotsList || []);
          const slotItem = activeSlots.find(s => s.id === p.slot_id || s.slot === p.slot_id || String(s.id) === String(p.slot_id) || s.slot_key === p.slot_id);
          const slotKey = slotItem ? slotItem.slot : p.slot_id;
          if (slotKey && loadedWeek[p.day_of_week]) {
            loadedWeek[p.day_of_week][slotKey] = {
              subjectId:  subjectIds[0] || null,
              teacherId:  teacherIds[0] || null,
              subjectIds: subjectIds,
              teacherIds: teacherIds,
              room:       p.room  || 'Own Classroom',
              type:       p.period_type || 'class',
              note:       p.note  || '',
              startTime:  p.start_time || null,
              endTime:    p.end_time || null,
            };
          }
        }
      });
      const key = this.getKey();
      this.setState(s => ({ timetable: { ...s.timetable, [key]: loadedWeek }, apiReady: true }));

      // 3. Load holidays for the current displayed month
      const month = weekStart.getMonth() + 1;
      const year  = weekStart.getFullYear();
      const holRes = await this.props.client.query({
        query: GET_HOLIDAYS,
        variables: { institute_id: instituteId, branch_name: selectedBranch, month, year },
        fetchPolicy: 'network-only',
      });
      const holRows = holRes.data?.getTimetableHolidays || [];
      const holidays    = {};
      const holidayDbIds = {};
      holRows.forEach(h => {
        holidays[h.holiday_date]    = { name: h.name, reason: h.reason || '', type: h.type || 'national' };
        holidayDbIds[h.holiday_date] = h.id;
      });
      this.setState({ holidays, holidayDbIds });

      // 3b. Load substitutions for this timetable container
      try {
        const subRes = await this.props.client.query({
          query: GET_SUBSTITUTIONS,
          variables: { institute_id: instituteId, timetable_id: timetableDbId },
          fetchPolicy: 'network-only',
        });
        const subRows = subRes.data?.getTimetableSubstitutions || [];
        const substituteMap = {};
        const activeSlots = mappedSlots.length > 0 ? mappedSlots : (this.state.slotsList || []);

        subRows.forEach(s => {
          const slotObj = activeSlots.find(item => item.id === s.slot_id || String(item.id) === String(s.slot_id) || item.slot === s.slot_id || item.slot_key === s.slot_id);
          const cleanSlotKey = slotObj ? slotObj.slot : s.slot_id;

          const subTch = (this.state.teachers || []).find(t => String(t.id) === String(s.substitute_id));
          const origTch = (this.state.teachers || []).find(t => String(t.id) === String(s.original_teacher_id));
          const subData = {
            substitute_id: s.substitute_id,
            substitute_name: subTch ? subTch.name : `Substitute #${s.substitute_id}`,
            original_teacher_name: origTch ? origTch.name : 'Original Teacher',
            subject_id: s.subject_id,
            reason: s.reason
          };

          substituteMap[`${s.substitution_date}_${s.slot_id}`] = subData;
          if (cleanSlotKey) {
            substituteMap[`${s.substitution_date}_${cleanSlotKey}`] = subData;
          }
        });
        this.setState({ substituteMap });
      } catch (subErr) {
        console.warn('[TimetableBuilder] Failed to load substitutions:', subErr);
      }

      // 4. Load templates
      const tplRes = await this.props.client.query({
        query: GET_TEMPLATES,
        variables: { institute_id: instituteId },
        fetchPolicy: 'network-only',
      });
      const tplRows = tplRes.data?.getTimetableTemplates || [];
      const templates = tplRows.map(t => ({
        id:      t.client_uid || String(t.id),
        dbId:    t.id,
        name:    t.name,
        context: t.context_hint || '',
        week:    (() => { try { return JSON.parse(t.weeks_data); } catch (e) { return emptyWeek(); } })(),
        savedAt: '',
      }));
      this.setState({ templates });

    } catch (err) {
      console.warn('[TimetableBuilder] API load failed:', err);
    }
  };

  // ─── Reload just holidays when navigating to a new month ─────────────────
  reloadHolidays = async () => {
    const { selectedBranch, weekStart } = this.state;
    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!instituteId || !this.props.client) return;
    try {
      const month = weekStart.getMonth() + 1;
      const year  = weekStart.getFullYear();
      const res = await this.props.client.query({
        query: GET_HOLIDAYS,
        variables: { institute_id: instituteId, branch_name: selectedBranch, month, year },
        fetchPolicy: 'network-only',
      });
      const holRows = res.data?.getTimetableHolidays || [];
      const holidays    = {};
      const holidayDbIds = {};
      holRows.forEach(h => {
        holidays[h.holiday_date]     = { name: h.name, reason: h.reason || '', type: h.type || 'national' };
        holidayDbIds[h.holiday_date] = h.id;
      });
      this.setState({ holidays, holidayDbIds });
    } catch (err) {
      console.warn('[TimetableBuilder] Holiday reload failed:', err);
    }
  };

  // ─── Lookups ──────────────────────────────────────────────────────────────
  getTeacher = (id) => this.state.teachers.find(t => t.id === parseInt(id)) || null;

  // ─── Subject management ───────────────────────────────────────────────────
  _persistSubjects = async (subjects) => {
    const { timetableDbId } = this.state;
    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!timetableDbId || !instituteId || !this.props.client) return;
    try {
      await this.props.client.mutate({
        mutation: SAVE_TIMETABLE_SUBJECTS,
        variables: { timetable_id: timetableDbId, institute_id: instituteId, subjects },
      });
    } catch (e) { console.warn('[TimetableBuilder] Subject save failed:', e); }
  };

  addSubject = async () => {
    if (!this.guard.requireEdit()) return;
    const { newSubjectName, newSubjectShort, newSubjectCode, newSubjectColor, subjects } = this.state;
    if (!newSubjectName.trim()) return;
    const maxId  = subjects.reduce((m, s) => Math.max(m, s.id), 0);
    const newS   = {
      id:    maxId + 1,
      name:  newSubjectName.trim(),
      short: (newSubjectShort.trim() || newSubjectName.trim().slice(0, 6)),
      code:  newSubjectCode.trim() || `SUB-${maxId + 1}`,
      color: newSubjectColor,
    };
    const updated = [...subjects, newS];
    _activeSubjects = updated;
    this.setState({
      subjects: updated,
      newSubjectName: '', newSubjectShort: '', newSubjectCode: '',
      newSubjectColor: SUBJECT_COLORS[(updated.length) % SUBJECT_COLORS.length],
    });
    await this._persistSubjects(updated);
  };

  deleteSubject = async (id) => {
    if (!this.guard.requireEdit()) return;
    const updated = this.state.subjects.filter(s => s.id !== id);
    _activeSubjects = updated;
    this.setState({ subjects: updated });
    await this._persistSubjects(updated);
  };

  // ─── Context ──────────────────────────────────────────────────────────────
  getKey(b, c, s) {
    return `${b || this.state.selectedBranch}||${c || this.state.selectedClass}||${s || this.state.selectedSection}`;
  }
  getWeek(b, c, s)           { return this.state.timetable[this.getKey(b, c, s)] || emptyWeek(); }
  getDaySchedule(d, b, c, s) {
    const { copyDateFrom, copyDateTo, weekStart } = this.state;
    if (copyDateFrom && copyDateTo && weekStart) {
      const targetDate = addDays(weekStart, (d || this.state.activeDay) - 1);
      const targetDateStr = fmtDate(targetDate);
      if (targetDateStr < copyDateFrom || targetDateStr > copyDateTo) {
        return emptyDay();
      }
    }
    return this.getWeek(b, c, s)[d] || emptyDay();
  }

  isOutOfTermRange = (d) => {
    const { copyDateFrom, copyDateTo, weekStart } = this.state;
    if (!copyDateFrom || !copyDateTo || !weekStart) return false;
    const targetDate = addDays(weekStart, (d || this.state.activeDay) - 1);
    const targetDateStr = fmtDate(targetDate);
    return targetDateStr < copyDateFrom || targetDateStr > copyDateTo;
  };
  getClassObj(label) {
    const { classesList } = this.state;
    return classesList.find(cl => cl.label === (label || this.state.selectedClass))
      || classesList[0]
      || { id: '', label: '', sections: [] };
  }

  updateWeek = (newWeek) => {
    const key = this.getKey();
    this.setState(s => ({ timetable: { ...s.timetable, [key]: newWeek } }));
  };
  updateDay = (day, newDay) => {
    const week = this.getWeek();
    this.updateWeek({ ...week, [day]: newDay });
  };

  // ─── Assign ───────────────────────────────────────────────────────────────
  openAssign = (slotInput, existing = null) => {
    const slotObj = (this.state.slotsList || []).find(s => s.id === slotInput || s.slot === slotInput || String(s.id) === String(slotInput) || s.slot_key === slotInput);
    const slot = slotObj ? slotObj.slot : slotInput;
    const firstSubj = (this.state.subjects && this.state.subjects.length > 0) ? this.state.subjects[0].id : '';
    this.setState({
      showAssignModal: true,
      assignSlot: slot,
      assignForm: existing
        ? { 
            subjectId:  existing.subjectId || (existing.subjectIds && existing.subjectIds[0]) || firstSubj, 
            teacherId:  existing.teacherId || (existing.teacherIds && existing.teacherIds[0]) || '', 
            room:       existing.room || 'Own Classroom', 
            note:       existing.note || '',
            type:       existing.type || 'class',
            startTime:  existing.startTime || '',
            endTime:    existing.endTime || '',
            subjectIds: existing.subjectIds || (existing.subjectId ? [existing.subjectId] : (firstSubj ? [firstSubj] : [])),
            teacherIds: existing.teacherIds || (existing.teacherId ? [existing.teacherId] : []),
          }
        : { ...EMPTY_FORM, subjectId: firstSubj, subjectIds: firstSubj ? [firstSubj] : [] },
    });
  };
  closeAssign = () => this.setState({ showAssignModal: false, assignSlot: null });
  setForm = (k, v) => this.setState(s => ({ assignForm: { ...s.assignForm, [k]: v } }));

  saveAssign = (e) => {
    e.preventDefault();
    const { activeDay, assignSlot, assignForm } = this.state;

    if (assignForm.type !== 'break' && assignForm.type !== 'free') {
      const teacherIds = Array.isArray(assignForm.teacherIds)
        ? assignForm.teacherIds
        : (assignForm.teacherId ? [parseInt(assignForm.teacherId)] : []);

      const clashes = this.detectClash(activeDay, assignSlot, teacherIds, assignForm.room);
      if (clashes.length > 0) {
        this.setState({
          clashWarning:  clashes,
          pendingAssign: { dayNum: activeDay, slot: assignSlot, form: { ...assignForm } },
        });
        return; // stop — let user decide via clash modal
      }
    }
    this.commitAssign(activeDay, assignSlot, assignForm);
  };

  // Called after clash check passes (or user forces override)
  commitAssign = (dayNum, slotInput, form) => {
    const slotObj = (this.state.slotsList || []).find(s => s.id === slotInput || s.slot === slotInput || String(s.id) === String(slotInput) || s.slot_key === slotInput);
    const slot = slotObj ? slotObj.slot : slotInput;
    const day    = this.getDaySchedule(dayNum);
    const subjectIds = Array.isArray(form.subjectIds) ? form.subjectIds : (form.subjectId ? [parseInt(form.subjectId)] : []);
    const teacherIds = Array.isArray(form.teacherIds) ? form.teacherIds : (form.teacherId ? [parseInt(form.teacherId)] : []);

    const newDay = {
      ...day,
      [slot]: {
        subjectId:  subjectIds[0] || null,
        teacherId:  teacherIds[0] || null,
        subjectIds: subjectIds,
        teacherIds: teacherIds,
        room:       form.room || 'Own Classroom',
        note:       form.note || '',
        type:       form.type || 'class',
        startTime:  form.startTime || null,
        endTime:    form.endTime   || null,
      },
    };
    this.updateDay(dayNum, newDay);
    this.setState({ showAssignModal: false, assignSlot: null, clashWarning: null, pendingAssign: null });
    this.syncDayToApi(dayNum, newDay);
  };

  // User chose to override clashes and save anyway
  forceAssign = () => {
    const { pendingAssign } = this.state;
    if (pendingAssign) this.commitAssign(pendingAssign.dayNum, pendingAssign.slot, pendingAssign.form);
  };

  clearSlot = (slot) => {
    if (!this.guard.requireEdit()) return;
    const { activeDay } = this.state;
    const day    = this.getDaySchedule(activeDay);
    const newDay = { ...day, [slot]: null };
    this.updateDay(activeDay, newDay);
    this.syncDayToApi(activeDay, newDay);
  };

  // ─── Auto-sync a single day's periods to DB ───────────────────────────────
  syncDayToApi = async (dayNum, dayData, overrideTimetableId) => {
    const timetableDbId = overrideTimetableId || this.state.timetableDbId;
    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!timetableDbId || !instituteId || !this.props.client) return;
    const periods = PERIOD_SLOTS
      .filter(s => dayData?.[s])
      .map(s => {
        const slotData = dayData[s];
        const subjectIdStr = Array.isArray(slotData.subjectIds)
          ? slotData.subjectIds.join(',')
          : slotData.subjectId ? String(slotData.subjectId) : '';
        const teacherIdStr = Array.isArray(slotData.teacherIds)
          ? slotData.teacherIds.join(',')
          : slotData.teacherId ? String(slotData.teacherId) : '';

        const slotObj = (this.state.slotsList || []).find(item => item.slot === s || item.slot_key === s);
        const resolvedSlotId = slotObj ? slotObj.id : (isNaN(parseInt(s)) ? s : parseInt(s));

        return {
          slot_id:     resolvedSlotId,
          period_type: slotData.type      || 'class',
          subject_id:  subjectIdStr || null,
          teacher_id:  teacherIdStr || null,
          room:        slotData.room      || 'Own Classroom',
          note:        slotData.note      || null,
        };
      });
    try {
      await this.props.client.mutate({
        mutation: SAVE_TIMETABLE_PERIODS,
        variables: { params: { institute_id: instituteId, timetable_id: timetableDbId, day_of_week: dayNum, periods } },
      });
    } catch (err) {
      console.warn('[TimetableBuilder] Auto-save period failed:', err);
    }
  };

  // ─── Copy ─────────────────────────────────────────────────────────────────
  openCopyModal = (mode) => {
    if (!this.guard.requireEdit()) return;
    this.setState({ showCopyModal: true, copyMode: mode, copyTargetDays: [] });
  };
  closeCopyModal = () => this.setState({ showCopyModal: false });
  toggleCopyDay = (d) => {
    this.setState(s => {
      const set = new Set(s.copyTargetDays);
      set.has(d) ? set.delete(d) : set.add(d);
      return { copyTargetDays: [...set] };
    });
  };
  copyDayToTargets = () => {
    const { activeDay, copyTargetDays } = this.state;
    const week    = this.getWeek();
    const src     = week[activeDay] || emptyDay();
    const newWeek = { ...week };
    copyTargetDays.forEach(d => { newWeek[d] = { ...src }; });
    this.updateWeek(newWeek);
    this.setState({ showCopyModal: false, copyTargetDays: [] });
    copyTargetDays.forEach(d => this.syncDayToApi(d, src));
  };
  copyWeekToSection = async () => {
    const { copyTargetBranch, copyTargetClass, copyTargetSection, classesList, sectionIdMap, selectedBranchId } = this.state;
    const targetKey = this.getKey(copyTargetBranch, copyTargetClass, copyTargetSection);
    const currentWeek = deepCloneWeek(this.getWeek());

    this.setState(s => ({
      timetable: { ...s.timetable, [targetKey]: currentWeek },
      showCopyModal: false,
    }));

    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!instituteId || !this.props.client) return;

    const targetClassObj = classesList.find(c => c.label === copyTargetClass || c.name === copyTargetClass);
    const targetClassId = targetClassObj ? parseInt(targetClassObj.id) : null;
    const targetSectionId = sectionIdMap[`${targetClassId}||${copyTargetSection}`] || null;

    if (!targetClassId || !targetSectionId) return;

    try {
      const ttRes = await this.props.client.mutate({
        mutation: UPSERT_TIMETABLE,
        variables: {
          params: {
            institute_id: instituteId,
            branch_id: selectedBranchId,
            class_id: targetClassId,
            section_id: targetSectionId,
          },
        },
      });
      const targetTimetableId = ttRes.data?.upsertTimetable?.id;
      if (!targetTimetableId) return;

      const savePromises = [1, 2, 3, 4, 5, 6].map(dayNum => {
        const dayData = currentWeek[dayNum];
        const periods = PERIOD_SLOTS
          .filter(s => dayData?.[s])
          .map(s => {
            const slotData = dayData[s];
            const subjectIdStr = Array.isArray(slotData.subjectIds)
              ? slotData.subjectIds.join(',')
              : slotData.subjectId ? String(slotData.subjectId) : '';
            const teacherIdStr = Array.isArray(slotData.teacherIds)
              ? slotData.teacherIds.join(',')
              : slotData.teacherId ? String(slotData.teacherId) : '';

            return {
              slot_id: parseInt(s),
              period_type: slotData.type || 'class',
              subject_id: subjectIdStr || null,
              teacher_id: teacherIdStr || null,
              room: slotData.room || 'Own Classroom',
              note: slotData.note || null,
            };
          });

        return this.props.client.mutate({
          mutation: SAVE_TIMETABLE_PERIODS,
          variables: {
            params: {
              institute_id: instituteId,
              timetable_id: targetTimetableId,
              day_of_week: dayNum,
              periods,
            },
          },
        });
      });

      await Promise.all(savePromises);
    } catch (err) {
      console.warn('[TimetableBuilder] copyWeekToSection database sync failed:', err);
    }
  };

  saveDateRange = async () => {
    const { copyDateFrom, copyDateTo, selectedBranchId, configDbId } = this.state;
    if (!copyDateFrom || !copyDateTo || copyDateFrom > copyDateTo) return;
    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!instituteId || !this.props.client) return;
    try {
      const res = await this.props.client.mutate({
        mutation: SAVE_TIMETABLE_CONFIG,
        variables: {
          params: {
            ...(configDbId ? { id: configDbId } : {}),
            institute_id:     instituteId,
            branch_id:        selectedBranchId,
            start_date:       copyDateFrom,
            end_date:         copyDateTo,
          },
        },
      });
      const id = res.data?.saveTimetableConfig?.id;
      if (id) this.setState({ configDbId: id });
      this.closeCopyModal();
    } catch (err) {
      console.warn('[TimetableBuilder] Date range save failed:', err);
    }
  };

  // ─── Templates ────────────────────────────────────────────────────────────
  saveTemplate = () => {
    if (!this.guard.requireEdit()) return;
    const { templateNameInput, selectedClass, selectedSection } = this.state;
    if (!templateNameInput.trim()) return;
    const clientUid = uid();
    const tpl = {
      id:      clientUid,
      dbId:    null,
      name:    templateNameInput.trim(),
      context: `${selectedClass} · Sec-${selectedSection}`,
      week:    deepCloneWeek(this.getWeek()),
      savedAt: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    };
    this.setState(s => ({ templates: [tpl, ...(s.templates || [])], showSaveTemplateModal: false, templateNameInput: '' }));
    this.syncTemplateSave(tpl);
  };

  applyTemplate = async (tpl) => {
    if (!this.guard.requireEdit()) return;
    const week = deepCloneWeek(tpl.week);
    this.updateWeek(week);
    this.setState({ showLoadTemplateModal: false });
    await this.saveTimetable();
  };

  deleteTemplate = (id) => {
    if (!this.guard.requireEdit()) return;
    const currentTemplates = this.state.templates || [];
    const tpl = currentTemplates.find(t => t.id === id);
    this.setState(s => ({ templates: (s.templates || []).filter(t => t.id !== id) }));
    if (tpl?.dbId) this.syncTemplateDelete(tpl.dbId);
  };

  syncTemplateSave = async (tpl) => {
    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!instituteId || !this.props.client) return;
    try {
      const res = await this.props.client.mutate({
        mutation: SAVE_TEMPLATE,
        variables: {
          params: {
            ...(tpl.dbId ? { id: tpl.dbId } : {}),
            institute_id: instituteId,
            name:         tpl.name,
            context_hint: tpl.context,
            client_uid:   tpl.id,
            weeks_data:   JSON.stringify(tpl.week),
          },
        },
      });
      const dbId = res.data?.saveTemplate?.id;
      if (dbId) {
        this.setState(s => ({
          templates: (s.templates || []).map(t => t.id === tpl.id ? { ...t, dbId } : t),
        }));
      }
    } catch (err) {
      console.warn('[TimetableBuilder] Template save failed:', err);
    }
  };

  syncTemplateDelete = async (dbId) => {
    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!instituteId || !this.props.client) return;
    try {
      await this.props.client.mutate({ mutation: DELETE_TEMPLATE, variables: { id: dbId, institute_id: instituteId } });
    } catch (err) {
      console.warn('[TimetableBuilder] Template delete failed:', err);
    }
  };
  saveTimetable = async () => {
    if (!this.guard.requireEdit()) return;
    let { timetableDbId } = this.state;
    if (!timetableDbId) {
      // Fetch/create the timetable container without overwriting local period state
      const { selectedBranchId, selectedClassId, selectedSectionId } = this.state;
      const instituteId = parseInt(Cookies.get('institutionid'));
      if (!instituteId || !selectedBranchId || !selectedClassId || !selectedSectionId) return;
      try {
        const ttRes = await this.props.client.mutate({
          mutation: UPSERT_TIMETABLE,
          variables: { params: { institute_id: instituteId, branch_id: selectedBranchId, class_id: selectedClassId, section_id: selectedSectionId } },
        });
        timetableDbId = ttRes.data?.upsertTimetable?.id;
        if (timetableDbId) this.setState({ timetableDbId });
      } catch (err) {
        console.warn('[TimetableBuilder] saveTimetable: upsert failed', err);
      }
    }
    if (!timetableDbId) return;
    const week = this.getWeek();
    await Promise.all([1,2,3,4,5,6].map(d => this.syncDayToApi(d, week[d], timetableDbId)));
    this.setState({ saveFlash: true });
    setTimeout(() => this.setState({ saveFlash: false }), 2000);
  };

  // ─── Bell schedule helpers ─────────────────────────────────────────────────
  getBellSchedule = () => {
    const { slotsList } = this.state;
    return slotsList && slotsList.length > 0 ? slotsList : [];
  };

  getDurationLabel = (start, end) => {
    if (!start || !end) return '';
    try {
      const [sH, sM] = start.split(':').map(Number);
      const [eH, eM] = end.split(':').map(Number);
      const diff = (eH * 60 + eM) - (sH * 60 + sM);
      return diff > 0 ? `${diff} min` : '';
    } catch (_) {
      return '';
    }
  };

  // ─── Clash detection ──────────────────────────────────────────────────────
  detectClash = (dayNum, slotKey, teacherIdInput, room) => {
    const clashes    = [];
    const currentKey = this.getKey();

    const checkTeacherIds = Array.isArray(teacherIdInput)
      ? teacherIdInput
      : (teacherIdInput ? [parseInt(teacherIdInput)] : []);

    Object.entries(this.state.timetable).forEach(([key, week]) => {
      if (key === currentKey) return;
      const entry = week?.[dayNum]?.[slotKey];
      if (!entry) return;
      const [branch, cls, section] = key.split('||');

      const entryTeacherIds = Array.isArray(entry.teacherIds)
        ? entry.teacherIds
        : (entry.teacherId ? [entry.teacherId] : []);

      if (checkTeacherIds.length > 0 && entryTeacherIds.length > 0) {
        checkTeacherIds.forEach(tId => {
          if (entryTeacherIds.includes(tId)) {
            const tch = this.getTeacher(tId);
            clashes.push({
              type:  'teacher',
              icon:  'fal fa-chalkboard-teacher',
              color: '#c0533a',
              title: 'Teacher Double-Booked',
              desc:  `${tch?.name || 'This teacher'} is already assigned to ${cls} · Sec-${section} (${branch}) at this time slot.`,
            });
          }
        });
      }

      if (room && room !== 'Own Classroom' && entry.room === room) {
        clashes.push({
          type:  'room',
          icon:  'fal fa-map-marker-alt',
          color: '#b07a10',
          title: 'Room Already Booked',
          desc:  `"${room}" is already allocated to ${cls} · Sec-${section} (${branch}) during this period.`,
        });
      }
    });

    return clashes;
  };

  triggerFlash = (msg) => {
    this.setState({ saveFlash: true, saveFlashMsg: msg || 'Timetable Saved Successfully!' });
    setTimeout(() => this.setState({ saveFlash: false, saveFlashMsg: '' }), 2500);
  };

  // ─── Bell config ──────────────────────────────────────────────────────────
  openBellConfig = () => {
    if (!this.guard.requireEdit()) return;
    this.setState({
      showBellConfigModal: true,
      bellConfigDraft: this.getBellSchedule().map(b => ({ ...b })),
      bellLoading: false,
      bellError: '',
    });
  };

  closeBellConfig = () => this.setState({ showBellConfigModal: false, bellConfigDraft: null, bellLoading: false });

  saveBellConfig = async () => {
    const { bellConfigDraft, selectedBranchId, timetableDbId, selectedClassId, bellScope } = this.state;
    if (!bellConfigDraft) return;

    this.setState({ bellLoading: true, bellError: '' });

    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!instituteId || !selectedBranchId || !this.props.client) {
      this.setState({ bellLoading: false });
      return;
    }

    const slotsPayload = bellConfigDraft.map((b, idx) => ({
      ...(b.id ? { id: parseInt(b.id) } : {}),
      slot_key: b.slot_key || String(b.slot),
      label: b.label,
      start_time: b.start.includes(':') && b.start.split(':').length === 2 ? b.start + ':00' : b.start,
      end_time: b.end.includes(':') && b.end.split(':').length === 2 ? b.end + ':00' : b.end,
      slot_order: idx + 1,
      slot_type: b.type,
      estatus: b.estatus != null ? b.estatus : 1
    }));

    const scopeVars = {
      section: { timetable_id: timetableDbId, class_id: null },
      class:   { timetable_id: null, class_id: selectedClassId },
      branch:  { timetable_id: null, class_id: null },
    }[bellScope || 'section'];

    try {
      await this.props.client.mutate({
        mutation: SAVE_TIMETABLE_SLOTS,
        variables: {
          institute_id: instituteId,
          branch_id: selectedBranchId,
          timetable_id: scopeVars.timetable_id,
          class_id: scopeVars.class_id,
          slots: slotsPayload
        }
      });
      await this.loadFromApi();
      this.setState({ showBellConfigModal: false, bellConfigDraft: null, bellLoading: false });
      this.triggerFlash('Bell schedule timings saved & updated!');
    } catch (err) {
      console.warn('[TimetableBuilder] saveBellConfig failed:', err);
      this.setState({ bellLoading: false, bellError: 'Failed to save bell schedule. Please try again.' });
    }
  };

  resetBellConfig = async () => {
    const { selectedBranchId, timetableDbId, selectedClassId, bellScope } = this.state;
    this.setState({ bellLoading: true, bellError: '' });

    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!instituteId || !selectedBranchId || !this.props.client) {
      this.setState({ bellLoading: false });
      return;
    }

    const scopeVars = {
      section: { timetable_id: timetableDbId, class_id: null },
      class:   { timetable_id: null, class_id: selectedClassId },
      branch:  { timetable_id: null, class_id: null },
    }[bellScope || 'section'];

    try {
      await this.props.client.mutate({
        mutation: SAVE_TIMETABLE_SLOTS,
        variables: {
          institute_id: instituteId,
          branch_id: selectedBranchId,
          timetable_id: scopeVars.timetable_id,
          class_id: scopeVars.class_id,
          slots: []
        }
      });
      await this.loadFromApi();
      this.setState({ showBellConfigModal: false, bellConfigDraft: null, bellLoading: false });
      this.triggerFlash('Bell schedule reset to defaults.');
    } catch (err) {
      console.warn('[TimetableBuilder] resetBellConfig failed:', err);
      this.setState({ bellLoading: false, bellError: 'Failed to reset bell schedule.' });
    }
  };


  setBellDraft = (slot, field, value) => this.setState(s => ({
    bellConfigDraft: s.bellConfigDraft.map(b => b.slot === slot ? { ...b, [field]: value } : b),
  }));

  deleteBellRow = (slot) => this.setState(s => ({
    bellConfigDraft: s.bellConfigDraft.filter(b => b.slot !== slot),
  }));

  moveBellRow = (slot, dir) => this.setState(s => {
    const draft = [...s.bellConfigDraft];
    const idx   = draft.findIndex(b => b.slot === slot);
    const to    = idx + dir;
    if (to < 0 || to >= draft.length) return null;
    [draft[idx], draft[to]] = [draft[to], draft[idx]];
    return { bellConfigDraft: draft };
  });

  addBellPeriod = () => this.setState(s => {
    const draft   = s.bellConfigDraft;
    const periods = draft.filter(b => b.type === 'period');
    const num     = periods.length + 1;
    // find a unique slot key
    let slot = `p${num}`;
    let i = num;
    while (draft.find(b => b.slot === slot)) { i++; slot = `p${i}`; }
    const last = draft[draft.length - 1];
    return {
      bellConfigDraft: [...draft, {
        slot, label: `Period ${num}`, type: 'period', num,
        start: last ? last.end : '08:00',
        end:   last ? this._addMinutes(last.end, 45) : '08:45',
      }],
    };
  });

  addBellBreak = () => this.setState(s => {
    const draft   = s.bellConfigDraft;
    const breaks  = draft.filter(b => b.type === 'break').length;
    const slot    = `break${breaks + 1}`;
    const last    = draft[draft.length - 1];
    return {
      bellConfigDraft: [...draft, {
        slot, label: 'Break', type: 'break',
        start: last ? last.end : '10:00',
        end:   last ? this._addMinutes(last.end, 15) : '10:15',
      }],
    };
  });

  _addMinutes = (timeStr, mins) => {
    const [h, m] = timeStr.split(':').map(Number);
    const total  = h * 60 + m + mins;
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  // ─── Print ────────────────────────────────────────────────────────────────
  handlePrint = () => {
    const { selectedBranch, selectedClass, selectedSection, weekStart } = this.state;
    const bellSch   = this.getBellSchedule();
    const weekRange = fmtWeekRange(weekStart);
    const today     = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

    // Collect subjects used this week for the legend
    const usedSubjectIds = new Set();
    [1,2,3,4,5,6].forEach(d => {
      const day = this.getDaySchedule(d);
      Object.values(day).forEach(e => { if (e?.subjectId) usedSubjectIds.add(e.subjectId); });
    });
    const legendHtml = [...usedSubjectIds].map(sid => {
      const s = getSubject(sid);
      if (!s) return '';
      return `<span class="leg-item"><span class="leg-dot" style="background:${s.color}"></span>${s.name}</span>`;
    }).join('');

    // Day header cells
    const dayHeaders = [1,2,3,4,5,6].map(d => {
      const date  = this.getDateForDay(d);
      const isHol = this.isHoliday(d);
      const hol   = this.getHoliday(d);
      return `<th class="day-th${isHol ? ' hol-th' : ''}">
        <div class="th-day">${DAYS[d]}</div>
        <div class="th-date">${fmtShort(date)}</div>
        ${isHol ? `<div class="th-hol-badge">${hol?.name || 'Holiday'}</div>` : ''}
      </th>`;
    }).join('');

    // Table body rows
    const tableRows = bellSch.map(bell => {
      if (bell.type === 'break' || bell.type === 'special') {
        const isAssembly = bell.type === 'special';
        const isLunch    = bell.slot === 'lunch';
        const rowCls  = isAssembly ? 'row-assembly' : isLunch ? 'row-lunch' : 'row-recess';
        const icon    = isAssembly ? '🙏' : isLunch ? '🍽️' : '☕';
        const dur     = isAssembly ? '20 min' : isLunch ? '45 min' : '15 min';
        return `<tr class="break-row ${rowCls}">
          <td class="pn-cell">${icon}</td>
          <td class="time-cell"><div class="t-start">${bell.start}</div><div class="t-end">${bell.end}</div></td>
          <td colspan="6" class="break-span"><strong>${bell.label}</strong><span class="break-dur">${dur}</span></td>
        </tr>`;
      }

      const cells = [1,2,3,4,5,6].map(d => {
        if (this.isHoliday(d)) {
          const hol = this.getHoliday(d);
          return `<td class="pd-cell hol-cell"><span class="hol-name">${hol?.name || 'Holiday'}</span></td>`;
        }
        const entry = this.getDaySchedule(d)[bell.slot];
        const subj  = entry ? getSubject(entry.subjectId) : null;
        const tch   = entry?.teacherId ? this.getTeacher(entry.teacherId) : null;
        if (!subj) return `<td class="pd-cell empty-cell"><span class="empty-dash">—</span></td>`;
        return `<td class="pd-cell filled-cell" style="border-top:3px solid ${subj.color};background:${subj.color}0e;">
          <div class="pd-subj" style="color:${subj.color}">${subj.name}</div>
          <div class="pd-code">${subj.code}</div>
          ${tch ? `<div class="pd-tch">${tch.initials} &middot; ${tch.subject.split(' ')[0]}</div>` : ''}
          ${entry.room && entry.room !== 'Own Classroom' ? `<div class="pd-room">${entry.room}</div>` : ''}
        </td>`;
      }).join('');

      return `<tr class="period-row">
        <td class="pn-cell"><div class="pn-badge">P${bell.num}</div></td>
        <td class="time-cell">
          <div class="t-start">${bell.start}</div>
          <div class="t-end">${bell.end}</div>
        </td>
        ${cells}
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Timetable — ${selectedClass} · Sec-${selectedSection}</title>
<style>
@page { size: A4 landscape; margin: 5mm 7mm; }
*,*::before,*::after { box-sizing:border-box; margin:0; padding:0; }
body { font-family:'Segoe UI',Arial,sans-serif; background:#fff; color:#0f172a;
       -webkit-print-color-adjust:exact; print-color-adjust:exact;
       display:flex; flex-direction:column; height:100vh; }

/* ── HEADER (legend lives inside here — no separate row) ── */
.hdr {
  display:flex; align-items:center; justify-content:space-between;
  background:#0f172a; color:#fff;
  padding:8px 14px; border-radius:8px; margin-bottom:5px; flex-shrink:0;
}
.hdr-left h1 { font-size:16px; font-weight:900; letter-spacing:-0.3px; line-height:1.2; }
.hdr-left p  { font-size:9.5px; color:#94a3b8; margin-top:2px; }
.hdr-right   { display:flex; align-items:flex-start; gap:8px; }
.hdr-chips   { display:flex; flex-direction:column; gap:4px; }
.chip-row    { display:flex; gap:6px; }
.hdr-chip    { background:#1e293b; border:1px solid #334155; border-radius:6px; padding:4px 10px; text-align:center; min-width:76px; }
.ck-label    { font-size:7.5px; color:#64748b; text-transform:uppercase; letter-spacing:0.6px; }
.ck-value    { font-size:10.5px; font-weight:800; color:#f1f5f9; margin-top:1px; }

/* Legend inside header */
.hdr-legend  { display:flex; flex-direction:column; justify-content:center; padding:4px 10px 4px 14px; border-left:1px solid #1e293b; gap:4px; }
.leg-title   { font-size:7px; color:#64748b; text-transform:uppercase; letter-spacing:0.5px; font-weight:700; }
.leg-grid    { display:flex; flex-wrap:wrap; gap:4px 10px; max-width:320px; }
.leg-item    { display:flex; align-items:center; gap:4px; font-size:8px; color:#cbd5e1; font-weight:600; white-space:nowrap; }
.leg-dot     { width:8px; height:8px; border-radius:2px; flex-shrink:0; }

/* ── TABLE ── */
.tt-table  { width:100%; border-collapse:collapse; table-layout:fixed; border:2px solid #0f172a; flex:1; }
col.col-pn { width:30px; }
col.col-t  { width:54px; }

thead tr   { border-bottom:2px solid #0f172a; }
.th-pn     { background:#0f172a; color:#475569; font-size:7.5px; text-transform:uppercase; letter-spacing:0.4px; padding:5px 3px; text-align:center; border-right:1px solid #1e293b; }
.th-time   { background:#1e293b; color:#64748b; font-size:7.5px; text-transform:uppercase; padding:5px 4px; text-align:center; border-right:2px solid #334155; }
.day-th    { padding:5px 4px; text-align:center; background:#f8fafc; border-right:1px solid #e2e8f0; border-bottom:2px solid #0f172a; }
.day-th:last-child { border-right:none; }
.th-day    { font-size:11px; font-weight:900; color:#0f172a; }
.th-date   { font-size:8px; color:#64748b; margin-top:1px; }
.th-hol    { font-size:6.5px; color:#dc2626; background:#fee2e2; border-radius:2px; padding:1px 3px; margin-top:2px; display:inline-block; font-weight:700; }
.hol-th    { background:#fff5f5 !important; }

/* Period rows */
.period-row  { border-bottom:1px solid #e2e8f0; }

.pn-cell   { text-align:center; background:#0f172a; border-right:1px solid #1e293b; border-bottom:1px solid #1e293b; vertical-align:middle; padding:3px 2px; width:30px; }
.pn-badge  { display:inline-flex; align-items:center; justify-content:center; width:22px; height:22px; background:#334155; color:#e2e8f0; border-radius:4px; font-size:9px; font-weight:800; }

.time-cell { text-align:center; vertical-align:middle; padding:3px; background:#f8fafc; border-right:2px solid #e2e8f0; border-bottom:1px solid #e2e8f0; }
.t-start   { font-size:10px; font-weight:800; color:#0f172a; white-space:nowrap; }
.t-end     { font-size:7.5px; color:#94a3b8; margin-top:1px; white-space:nowrap; }

.pd-cell   { vertical-align:middle; text-align:center; padding:3px 4px; border-right:1px solid #e2e8f0; border-bottom:1px solid #e2e8f0; overflow:hidden; }
.pd-cell:last-child { border-right:none; }
.filled-cell { background:#fff; }

.pd-subj   { font-size:9.5px; font-weight:900; line-height:1.2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.pd-code   { font-size:7px; color:#94a3b8; font-family:'Courier New',monospace; margin-top:1px; }
.pd-tch    { font-size:8px; color:#475569; margin-top:2px; font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.pd-room   { font-size:7px; color:#94a3b8; background:#f1f5f9; border-radius:2px; padding:0 3px; margin-top:2px; display:inline-block; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:100%; }

.empty-cell { color:#e2e8f0; }
.empty-dash { font-size:12px; }
.hol-cell   { background:#fff5f5 !important; border-top:3px solid #dc2626 !important; }
.hol-name   { font-size:7.5px; color:#dc2626; font-weight:700; }

/* Break rows — compact single line */
.break-row td   { padding:3px 8px; border-bottom:1px solid #e2e8f0; }
.break-span     { font-size:9.5px; font-weight:700; vertical-align:middle; }
.break-dur      { font-size:7.5px; font-weight:400; margin-left:6px; opacity:0.55; }
.row-assembly td { background:#eff6ff; } .row-assembly .break-span { color:#1d4ed8; }
.row-recess   td { background:#fffbeb; } .row-recess   .break-span { color:#92400e; }
.row-lunch    td { background:#fff7ed; } .row-lunch    .break-span { color:#c2410c; }

/* Footer */
.footer { display:flex; justify-content:space-between; align-items:center;
          margin-top:4px; border-top:1px solid #e2e8f0; padding-top:3px;
          font-size:7px; color:#94a3b8; flex-shrink:0; }
.footer strong { color:#475569; }
</style>
</head>
<body>

<div class="hdr">
  <div class="hdr-left">
    <h1>${selectedClass} &nbsp;&middot;&nbsp; Section ${selectedSection}</h1>
    <p>${selectedBranch} &nbsp;&middot;&nbsp; Weekly Class Timetable &nbsp;&middot;&nbsp; 8 Periods &times; 45 min</p>
  </div>
  <div class="hdr-right">
    ${legendHtml ? `<div class="hdr-legend">
      <div class="leg-title">Subjects</div>
      <div class="leg-grid">${legendHtml}</div>
    </div>` : ''}
    <div class="hdr-chips">
      <div class="chip-row">
        <div class="hdr-chip"><div class="ck-label">Week</div><div class="ck-value" style="font-size:9.5px">${weekRange}</div></div>
        <div class="hdr-chip"><div class="ck-label">Printed</div><div class="ck-value" style="font-size:9.5px">${today}</div></div>
      </div>
    </div>
  </div>
</div>

<table class="tt-table">
  <colgroup>
    <col class="col-pn"><col class="col-t">
    <col><col><col><col><col><col>
  </colgroup>
  <thead>
    <tr>
      <th class="th-pn">#</th>
      <th class="th-time">Time</th>
      ${dayHeaders}
    </tr>
  </thead>
  <tbody>${tableRows}</tbody>
</table>

<div class="footer">
  <span><strong>Rizee College ERP</strong> &nbsp;&middot;&nbsp; Auto-generated</span>
  <span>${selectedClass} &middot; Sec-${selectedSection} &middot; ${selectedBranch}</span>
  <span>For Classroom Display Only &nbsp;&middot;&nbsp; Confidential</span>
</div>

<script>window.onload=function(){setTimeout(function(){window.print();},350);};<\/script>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=1200,height=860');
    if (!win) { alert('Allow pop-ups for this page to print the timetable.'); return; }
    win.document.write(html);
    win.document.close();
  };

  // ─── Week navigation ──────────────────────────────────────────────────────
  prevWeek  = () => this.setState(s => ({ weekStart: addDays(s.weekStart, -7) }));
  nextWeek  = () => this.setState(s => ({ weekStart: addDays(s.weekStart,  7) }));
  goToToday = () => this.setState({ weekStart: getMonday(new Date()) });

  getDateForDay = (dayNum) => addDays(this.state.weekStart, dayNum - 1);
  getDayKey     = (dayNum) => fmtDate(this.getDateForDay(dayNum));
  isHoliday     = (dayNum) => !!this.state.holidays[this.getDayKey(dayNum)];
  getHoliday    = (dayNum) => this.state.holidays[this.getDayKey(dayNum)] || null;

  // ─── Holiday actions ──────────────────────────────────────────────────────
  openHolidayModal = (dayNum) => {
    if (!this.guard.requireEdit()) return;
    const existing = this.getHoliday(dayNum);
    this.setState({
      showHolidayModal:  true,
      holidayTargetDay:  dayNum,
      holidayForm: existing
        ? { name: existing.name, reason: existing.reason || '', type: existing.type || 'national' }
        : { name: '', reason: '', type: 'national' },
    });
  };
  closeHolidayModal = () => this.setState({ showHolidayModal: false, holidayTargetDay: null });

  saveHoliday = (e) => {
    e.preventDefault();
    const { holidayTargetDay, holidayForm } = this.state;
    if (!holidayForm.name.trim()) return;
    const key     = this.getDayKey(holidayTargetDay);
    const holData = { name: holidayForm.name.trim(), reason: holidayForm.reason.trim(), type: holidayForm.type };
    this.setState(s => ({
      holidays: { ...s.holidays, [key]: holData },
      showHolidayModal: false,
      holidayTargetDay: null,
    }));
    this.syncHolidayToApi(key, holData);
  };

  removeHoliday = (dayNum) => {
    if (!this.guard.requireEdit()) return;
    const key  = this.getDayKey(dayNum);
    const dbId = this.state.holidayDbIds[key];
    this.setState(s => {
      const h = { ...s.holidays };
      delete h[key];
      const ids = { ...s.holidayDbIds };
      delete ids[key];
      return { holidays: h, holidayDbIds: ids };
    });
    if (dbId) this.syncHolidayDelete(dbId);
  };

  syncHolidayToApi = async (dateKey, holData) => {
    const { holidayDbIds, selectedBranch } = this.state;
    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!instituteId || !this.props.client) return;
    try {
      const res = await this.props.client.mutate({
        mutation: SAVE_HOLIDAY,
        variables: {
          params: {
            id:             holidayDbIds[dateKey] || undefined,
            institute_id:   instituteId,
            holiday_date:   dateKey,
            name:           holData.name,
            reason:         holData.reason || null,
            type:           holData.type   || 'national',
            exception_type: 'holiday',
            branch_name:    selectedBranch,
          },
        },
      });
      const dbId = res.data?.saveHoliday?.id;
      if (dbId) this.setState(s => ({ holidayDbIds: { ...s.holidayDbIds, [dateKey]: dbId } }));
    } catch (err) {
      console.warn('[TimetableBuilder] Holiday save failed:', err);
    }
  };

  syncHolidayDelete = async (dbId) => {
    const instituteId = parseInt(Cookies.get('institutionid'));
    if (!instituteId || !this.props.client) return;
    try {
      await this.props.client.mutate({ mutation: DELETE_HOLIDAY, variables: { id: dbId, institute_id: instituteId } });
    } catch (err) {
      console.warn('[TimetableBuilder] Holiday delete failed:', err);
    }
  };

  setHolForm = (k, v) => this.setState(s => ({ holidayForm: { ...s.holidayForm, [k]: v } }));

  // ─── Render: week navigator ──────────────────────────────────────────────
  renderWeekNav() {
    const { weekStart, showCalendarPicker } = this.state;
    const today     = getMonday(new Date());
    const isToday   = fmtDate(weekStart) === fmtDate(today);

    return (
      <div className="ttb-week-nav">
        <button className="wn-arrow" onClick={this.prevWeek} title="Previous week">
          <i className="fal fa-chevron-left" />
        </button>
        <div className="wn-label">
          <i className="fal fa-calendar-week" />
          <span>{fmtWeekRange(weekStart)}</span>
        </div>
        <button className="wn-arrow" onClick={this.nextWeek} title="Next week">
          <i className="fal fa-chevron-right" />
        </button>
        <div className="wn-divider" />
        <div style={{ position: 'relative' }}>
          <button className="wn-cal-btn" onClick={() => this.setState(s => ({ showCalendarPicker: !s.showCalendarPicker }))} title="Open calendar">
            <i className="fal fa-calendar-alt" />
          </button>
          {showCalendarPicker && this.renderCalendarPicker()}
        </div>
        {!isToday && (
          <button className="wn-today-btn" onClick={this.goToToday}>
            <i className="fal fa-crosshairs" /> Today
          </button>
        )}
      </div>
    );
  }

  // ─── Render: mini calendar picker ────────────────────────────────────────
  renderCalendarPicker() {
    const { calPickerMonth, weekStart, holidays } = this.state;
    const year       = calPickerMonth.getFullYear();
    const month      = calPickerMonth.getMonth();
    const firstDay   = new Date(year, month, 1);
    const startPad   = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const startCell  = addDays(firstDay, -startPad);
    const today      = new Date(); today.setHours(0,0,0,0);
    const selWeekStr = fmtDate(weekStart);

    const cells = Array.from({ length: 42 }, (_, i) => addDays(startCell, i));

    return (
      <div className="ttb-cal-picker">
        <div className="tcp-head">
          <button className="tcp-nav" onClick={() => this.setState(s => ({
            calPickerMonth: new Date(s.calPickerMonth.getFullYear(), s.calPickerMonth.getMonth() - 1, 1)
          }))}><i className="fal fa-chevron-left" /></button>
          <span className="tcp-month">
            {calPickerMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
          </span>
          <button className="tcp-nav" onClick={() => this.setState(s => ({
            calPickerMonth: new Date(s.calPickerMonth.getFullYear(), s.calPickerMonth.getMonth() + 1, 1)
          }))}><i className="fal fa-chevron-right" /></button>
        </div>
        <div className="tcp-daynames">
          {['M','T','W','T','F','S','S'].map((d, i) => (
            <div key={i} className={`tcp-dn ${i >= 5 ? 'wknd' : ''}`}>{d}</div>
          ))}
        </div>
        <div className="tcp-grid">
          {cells.map((date, i) => {
            const isThisMonth   = date.getMonth() === month;
            const mondayOfCell  = getMonday(date);
            const inSelWeek     = fmtDate(mondayOfCell) === selWeekStr;
            const isToday2      = fmtDate(date) === fmtDate(today);
            const dow           = date.getDay();
            const isWeekend     = dow === 0 || dow === 6;
            const holKey        = fmtDate(date);
            const hasHoliday    = !!holidays[holKey];
            return (
              <div key={i}
                className={[
                  'tcp-cell',
                  !isThisMonth ? 'other-m' : '',
                  inSelWeek    ? 'in-sel'  : '',
                  isToday2     ? 'is-today': '',
                  isWeekend    ? 'wknd'    : '',
                  hasHoliday   ? 'has-hol' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => {
                  const goDay = dow === 0 ? 7 : dow;
                  this.setState({ weekStart: mondayOfCell, activeDay: Math.min(goDay, 6), showCalendarPicker: false });
                }}>
                <span className="tcp-num">{date.getDate()}</span>
                {hasHoliday && <span className="tcp-hol-dot" />}
              </div>
            );
          })}
        </div>
        <div className="tcp-footer">
          <button className="tcp-today-btn" onClick={() => {
            const td = getMonday(new Date());
            this.setState({ weekStart: td, showCalendarPicker: false, calPickerMonth: new Date(td.getFullYear(), td.getMonth(), 1) });
          }}>
            <i className="fal fa-crosshairs" /> Jump to Today
          </button>
        </div>
      </div>
    );
  }

  // ─── Render: holiday modal ────────────────────────────────────────────────
  renderHolidayModal() {
    const { showHolidayModal, holidayTargetDay, holidayForm } = this.state;
    if (!showHolidayModal) return null;
    const dayDate  = holidayTargetDay ? this.getDateForDay(holidayTargetDay) : null;
    const existing = holidayTargetDay ? this.getHoliday(holidayTargetDay) : null;
    const hType    = HOLIDAY_TYPES.find(h => h.key === holidayForm.type) || HOLIDAY_TYPES[0];

    return (
      <Modal show={showHolidayModal} onHide={this.closeHolidayModal} centered size="sm" className="ttb-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="modal-title-icon" style={{ background: hType.bg, color: hType.color }}>
              <i className={hType.icon} />
            </span>
            {existing ? 'Edit Holiday' : 'Mark as Holiday'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={this.saveHoliday}>

            {dayDate && (
              <div className="ttb-modal-section" style={{ padding: '12px 24px' }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#6b6b78' }}>
                  <i className="fal fa-calendar-day" style={{ marginRight: 6 }} />
                  {fmtFull(dayDate)}
                </div>
              </div>
            )}

            {/* Holiday type */}
            <div className="ttb-modal-section">
              <div className="ttb-modal-section-label">Holiday Type</div>
              <div className="hol-type-grid">
                {HOLIDAY_TYPES.map(ht => (
                  <button key={ht.key} type="button"
                    className={`hol-type-card ${holidayForm.type === ht.key ? 'selected' : ''}`}
                    style={holidayForm.type === ht.key ? { background: ht.bg, borderColor: ht.color, color: ht.color } : {}}
                    onClick={() => this.setHolForm('type', ht.key)}>
                    <i className={ht.icon} />
                    <span>{ht.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Holiday name */}
            <div className="ttb-modal-section">
              <div className="ttb-modal-section-label">Holiday Name *</div>
              <Form.Control type="text"
                value={holidayForm.name}
                onChange={e => this.setHolForm('name', e.target.value)}
                placeholder="e.g. Diwali, Independence Day…"
                className="ttb-form-input"
                autoFocus />
            </div>

            {/* Reason */}
            <div className="ttb-modal-section bg-soft">
              <div className="ttb-modal-section-label">Reason / Note</div>
              <Form.Control as="textarea" rows={2}
                value={holidayForm.reason}
                onChange={e => this.setHolForm('reason', e.target.value)}
                placeholder="e.g. National festival, School function…"
                className="ttb-form-input"
                style={{ resize: 'none' }} />
            </div>

            <div className="ttb-modal-footer">
              <button type="button" className="ttb-btn outline" onClick={this.closeHolidayModal}>Cancel</button>
              <button type="submit" className="ttb-btn"
                disabled={!holidayForm.name.trim()}
                style={{ background: hType.color, borderColor: hType.color, color: '#fff', opacity: holidayForm.name.trim() ? 1 : 0.45 }}>
                <i className="fal fa-check" style={{ marginRight: 6 }} />
                {existing ? 'Update' : 'Mark Holiday'}
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    );
  }

  // ─── Render: holiday banner (shown instead of period grid) ────────────────
  renderHolidayBanner(dayNum) {
    const hol   = this.getHoliday(dayNum);
    const hType = HOLIDAY_TYPES.find(h => h.key === hol.type) || HOLIDAY_TYPES[0];
    const date  = this.getDateForDay(dayNum);
    return (
      <div className="ttb-holiday-banner" style={{ background: hType.bg, borderColor: hType.border }}>
        <div className="hb-icon" style={{ background: hType.color }}>
          <i className={hType.icon} />
        </div>
        <div className="hb-body">
          <div className="hb-type" style={{ color: hType.color }}>{hType.label.toUpperCase()} HOLIDAY</div>
          <div className="hb-name">{hol.name}</div>
          {hol.reason && <div className="hb-reason">{hol.reason}</div>}
          <div className="hb-date">{fmtFull(date)} · All classes cancelled</div>
        </div>
        <div className="hb-actions">
          <button className="ttb-btn outline sm" onClick={() => this.openHolidayModal(dayNum)}>
            <i className="fal fa-pen" /> Edit
          </button>
          <button className="ttb-btn danger sm" onClick={() => this.removeHoliday(dayNum)}>
            <i className="fal fa-times" /> Remove
          </button>
        </div>
      </div>
    );
  }

  // ─── Render: context bar ─────────────────────────────────────────────────
  renderContextBar() {
    const { selectedBranch, selectedClass, selectedSection, branches, classesList } = this.state;
    const classObj = this.getClassObj();
    const week     = this.getWeek();
    return (
      <div className="ttb-context-bar">
        <span className="ctx-label">Branch</span>
        <select className="ctx-select" value={selectedBranch}
          onChange={e => {
            const br = branches.find(b => b.name === e.target.value);
            this.setState({ selectedBranch: e.target.value, selectedBranchId: br?.id || null, selectedClass: '', selectedSection: '' }, () => this.loadBranchData());
          }}>
          {branches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
        </select>
        <div className="ctx-divider" />
        <span className="ctx-label">Class</span>
        <select className="ctx-select" value={selectedClass}
          onChange={e => {
            const cls = classesList.find(c => c.label === e.target.value) || classesList[0];
            const firstSec  = cls?.sections[0] || '';
            const classId   = parseInt(cls?.id);
            const sectionId = this.state.sectionIdMap[`${classId}||${firstSec}`] || null;
            this.setState({ selectedClass: e.target.value, selectedClassId: classId, selectedSection: firstSec, selectedSectionId: sectionId });
          }}>
          {classesList.map(c => <option key={c.id} value={c.label}>{c.label}</option>)}
        </select>
        <div className="ctx-divider" />
        <span className="ctx-label">Section</span>
        <select className="ctx-select" value={selectedSection}
          onChange={e => {
            const sec = e.target.value;
            const classId = parseInt(classObj?.id || this.state.selectedClassId);
            const sectionId = this.state.sectionIdMap[`${classId}||${sec}`] || null;
            this.setState({ selectedSection: sec, selectedSectionId: sectionId });
          }}>
          {classObj.sections.map(sec => (
            <option key={`${classObj.id}-${sec}`} value={sec}>{sec}</option>
          ))}
        </select>
        <div style={{ marginLeft: 'auto' }} />
        <div className="ctx-week-summary">
          {[1,2,3,4,5,6].map(d => {
            const cnt = filledCount(week[d]);
            return (
              <div key={d}
                className={`ctx-day-dot ${cnt > 0 ? 'has-periods' : ''} ${cnt >= PERIOD_SLOTS.length ? 'full' : ''}`}
                title={`${DAYS[d]}: ${cnt}/${PERIOD_SLOTS.length}`}
                onClick={() => this.setState({ activeDay: d })}
                style={{ cursor: 'pointer' }}>
                <span className="dot-label">{DAYS_SHORT[d]}</span>
                <span className="dot-count">{cnt}/{PERIOD_SLOTS.length}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── Render: period grid (Academia-style card rows) ──────────────────────
  renderPeriodGrid() {
    const { activeDay, viewMode } = this.state;
    if (this.isHoliday(activeDay)) return this.renderHolidayBanner(activeDay);
    const daySchedule = this.getDaySchedule(activeDay);
    const isStudent   = viewMode === 'student';

    return (
      <div className="ttb-period-grid">
        {this.getBellSchedule().map(bell => {
          const slotId = bell.slot || bell.slot_key || bell.id;
          const slotType = bell.slot_type || bell.type;
          const label = bell.label || bell.slot;
          const startTime = bell.start_time || bell.start;
          const endTime = bell.end_time || bell.end;
          const slotOrder = bell.slot_order || bell.num;

          // ── Break / Assembly divider rows ──────────────────────────────────
          if (slotType === 'break' || slotType === 'special') {
            const isAssembly = slotType === 'special';
            const dur  = this.getDurationLabel(startTime, endTime) || (isAssembly ? '20 min' : '15 min');
            const icon = isAssembly ? 'fal fa-hands' : (label.toLowerCase() === 'lunch' || label.toLowerCase() === 'lunch break') ? 'fal fa-utensils' : 'fal fa-coffee';
            return (
              <div key={slotId} className={`prd-divider ${isAssembly ? 'assembly' : 'break'}`}>
                <span className="pd-time">{startTime} – {endTime}</span>
                <i className={icon} />
                <span className="pd-label">{label}</span>
                <span className="pd-dur">{dur}</span>
              </div>
            );
          }

          // ── Regular period rows ────────────────────────────────────────────
          const entry = daySchedule[slotId];
          
          let subjs = [];
          if (entry) {
            if (Array.isArray(entry.subjectIds) && entry.subjectIds.length > 0) {
              subjs = entry.subjectIds.map(sid => getSubject(sid)).filter(Boolean);
            } else if (entry.subjectId) {
              const singleSub = getSubject(entry.subjectId);
              if (singleSub) subjs = [singleSub];
            }
          }
          const primarySubj = subjs[0] || null;
          const color       = primarySubj ? primarySubj.color : null;
          const ptype       = entry?.type || 'class';

          let tchs = [];
          if (entry) {
            if (Array.isArray(entry.teacherIds) && entry.teacherIds.length > 0) {
              tchs = entry.teacherIds.map(tid => this.getTeacher(tid)).filter(Boolean);
            } else if (entry.teacherId) {
              const singleTch = this.getTeacher(entry.teacherId);
              if (singleTch) tchs = [singleTch];
            }
          }
          const primaryTch = tchs[0] || null;

          return (
            <div key={slotId}
              className={`prd-row ${entry ? 'filled' : 'empty'}`}
              onClick={() => this.openAssign(slotId, entry || null)}>

              {/* Left: time column */}
              <div className="prd-time-col">
                <span className="prd-t-start">{entry?.startTime || startTime}</span>
                <span className="prd-t-arrow">→ {entry?.endTime || endTime}</span>
                <span className="prd-t-dur">{this.getDurationLabel(entry?.startTime || startTime, entry?.endTime || endTime)}</span>
              </div>

              {/* Card */}
              <div className="prd-card" style={color ? { borderLeftColor: color } : {}}>
                {primarySubj ? (
                  <>
                    {/* Teacher avatar */}
                    <div className="prd-avatar"
                      style={{ background: `${color}18`, color, border: `1.5px solid ${color}35` }}>
                      {primaryTch ? primaryTch.initials : primarySubj.short.slice(0, 2).toUpperCase()}
                    </div>

                    {/* Subject info */}
                    <div className="prd-info">
                      <div className="prd-top">
                        <span className="prd-num">P{slotOrder}</span>
                        <span className={`prd-type-tag t-${ptype}`}>{ptype.toUpperCase()}</span>
                        <span className="prd-subj">{subjs.map(s => s.name).join(' / ')}</span>
                      </div>
                      <div className="prd-meta">
                        <span className="prd-code">{subjs.map(s => s.code).join(', ')}</span>
                        {tchs.length > 0 && <span className="prd-meta-sep">·</span>}
                        {tchs.length > 0 && <span className="prd-tch-name">{tchs.map(t => t.name).join(', ')}</span>}
                        {entry.note && <span className="prd-note">— {entry.note}</span>}
                      </div>

                      {/* Substitution badge if active */}
                      {(() => {
                        const targetDate = addDays(this.state.weekStart, activeDay - 1);
                        const dateStr = fmtDate(targetDate);
                        const activeSub = this.state.substituteMap[`${dateStr}_${slotId}`] ||
                                          this.state.substituteMap[`${dateStr}_${bell.id}`] ||
                                          this.state.substituteMap[`${dateStr}_${bell.slot_key}`];
                        if (!activeSub) return null;
                        const subSubj = activeSub.subject_id ? getSubject(activeSub.subject_id) : null;
                        return (
                          <div className="prd-substitute-badge" style={{ background: '#f0ebfb', border: '1px solid #d5c7f0', color: '#6d28d9', padding: '3px 8px', borderRadius: 6, marginTop: 4, fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                            <i className="fal fa-exchange-alt" />
                            <span>Sub: <strong>{activeSub.substitute_name}</strong> {subSubj ? `(${subSubj.name})` : ''}</span>
                            <span className="sub-orig" style={{ color: '#7c3aed', opacity: 0.8 }}>(was: {activeSub.original_teacher_name || primaryTch?.name})</span>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Room */}
                    <div className="prd-room">
                      <span className="prd-room-badge">
                        <i className="fal fa-map-marker-alt" />
                        {entry.room || 'Classroom'}
                      </span>
                    </div>
                  </>
                ) : (
                  /* Empty slot CTA */
                  <div className="prd-empty-content">
                    <div className="prd-empty-num">P{slotOrder}</div>
                    <div className="prd-empty-label">
                      <i className="fal fa-plus-circle" />
                      {isStudent ? 'No class assigned' : 'Click to assign subject'}
                    </div>
                    <div className="prd-empty-time">{startTime} – {endTime}</div>
                  </div>
                )}

                {/* Action buttons — edit mode only */}
                <div className="prd-actions" onClick={e => e.stopPropagation()}>
                  {entry ? (
                    <>
                      <button className="ttb-icon-btn substitute" title="Assign Substitute Teacher for this period"
                        onClick={() => this.openSubstituteModal(slotId, entry, bell)}>
                        <i className="fal fa-exchange-alt" />
                      </button>
                      <button className="ttb-icon-btn edit" title="Edit"
                        onClick={() => this.openAssign(slotId, entry)}>
                        <i className="fal fa-pen" />
                      </button>
                      <button className="ttb-icon-btn delete" title="Clear"
                        onClick={() => this.clearSlot(slotId)}>
                        <i className="fal fa-times" />
                      </button>
                    </>
                  ) : (
                    <button className="ttb-icon-btn add-btn"
                      onClick={() => this.openAssign(slotId)}>
                      <i className="fal fa-plus" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ─── Render: staff view (teacher workload table) ──────────────────────────
  renderStaffView() {
    const { activeDay } = this.state;
    const daySchedule   = this.getDaySchedule(activeDay);

    // Build map: teacherId → [{ bell, entry, subj }]
    const teacherMap = {};
    PERIOD_SLOTS.forEach(slot => {
      const entry = daySchedule[slot];
      if (!entry?.teacherId) return;
      if (!teacherMap[entry.teacherId]) teacherMap[entry.teacherId] = [];
      const bell = this.getBellSchedule().find(b => b.slot === slot);
      teacherMap[entry.teacherId].push({ bell, entry, subj: getSubject(entry.subjectId) });
    });

    const assignedTeachers = this.state.teachers.filter(t => teacherMap[t.id]);

    if (!assignedTeachers.length) {
      return (
        <div className="ttb-empty">
          <div className="ttb-empty-icon"><i className="fal fa-chalkboard-teacher" /></div>
          <h5>No staff assigned</h5>
          <p>Switch to Student View and assign subjects to periods first.</p>
        </div>
      );
    }

    return (
      <div className="staff-schedule-list">
        {assignedTeachers.map(teacher => {
          const periods = teacherMap[teacher.id];
          const totalMin = periods.length * 45;
          return (
            <div key={teacher.id} className="staff-row">
              {/* Teacher identity */}
              <div className="sr-identity">
                <div className="sr-avatar">{teacher.initials}</div>
                <div className="sr-info">
                  <div className="sr-name">{teacher.name}</div>
                  <div className="sr-dept">{teacher.subject}</div>
                </div>
                <div className="sr-stats">
                  <span className="sr-stat-num">{periods.length}p</span>
                  <span className="sr-stat-lbl">{totalMin} min</span>
                </div>
              </div>

              {/* Period cards for this teacher */}
              <div className="sr-periods">
                {periods.map(({ bell, entry, subj }) => {
                  const color = subj ? subj.color : '#a8a8b4';
                  return (
                    <div key={bell.slot} className="sr-period-card"
                      style={{ borderLeft: `3px solid ${color}` }}
                      onClick={() => this.openAssign(bell.slot, entry)}>
                      <div className="srp-time">{bell.start}</div>
                      <div className="srp-body">
                        <div className="srp-top">
                          <span className="srp-num" style={{ color }}>P{bell.num}</span>
                          <span className="srp-subj">{subj?.name || '—'}</span>
                        </div>
                        <div className="srp-meta">{subj?.code} · {entry.room}</div>
                      </div>
                      <button className="ttb-icon-btn edit" title="Edit"
                        onClick={e => { e.stopPropagation(); this.openAssign(bell.slot, entry); }}>
                        <i className="fal fa-pen" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ─── Render: week fill bars ───────────────────────────────────────────────
  renderWeekFill() {
    const week = this.getWeek();
    return [1,2,3,4,5,6].map(d => {
      const cnt = filledCount(week[d]);
      const max = PERIOD_SLOTS.length;
      const pct = Math.round((cnt / max) * 100);
      return (
        <div key={d} className="week-fill-row" onClick={() => this.setState({ activeDay: d })}
          style={{ cursor: 'pointer' }}>
          <span className="wfr-day">{DAYS_SHORT[d]}</span>
          <div className="wfr-bar-wrap">
            <div className="wfr-bar" style={{ width: `${pct}%`, background: pct === 100 ? '#3d8a55' : '#36a1f5' }} />
          </div>
          <span className="wfr-count" style={{ color: pct === 100 ? '#3d8a55' : cnt === 0 ? '#c8cad8' : '#36a1f5' }}>
            {cnt}/{max}
          </span>
        </div>
      );
    });
  }

  // ─── Render: teacher load ─────────────────────────────────────────────────
  renderTeacherLoad() {
    const day = this.getDaySchedule(this.state.activeDay);
    const map = {};
    PERIOD_SLOTS.forEach(s => {
      const e = day[s];
      if (e?.teacherId) { map[e.teacherId] = (map[e.teacherId] || 0) + 1; }
    });
    const entries = Object.entries(map);
    if (!entries.length) return <div style={{ padding: '8px 14px', fontSize: 12, color: '#a8a8b4' }}>No teachers assigned today.</div>;
    return entries.map(([id, cnt]) => {
      const t = this.getTeacher(id);
      if (!t) return null;
      return (
        <div key={id} className="ttb-lecturer-card">
          <div className="lc-avatar">{t.initials}</div>
          <div className="lc-info">
            <div className="lc-name">{t.name}</div>
            <div className="lc-load">{t.subject}</div>
          </div>
          <span className="lc-periods">{cnt}p</span>
        </div>
      );
    });
  }

  // ─── Render: assign modal ─────────────────────────────────────────────────
  renderAssignModal() {
    const { showAssignModal, assignSlot, assignForm, activeDay,
            selectedClass, selectedSection } = this.state;
    if (!showAssignModal) return null;
    const bell = this.getBellSchedule().find(b => b.slot === assignSlot || b.id === assignSlot || String(b.id) === String(assignSlot) || b.slot_key === assignSlot)
      || { slot: assignSlot || 'p1', label: 'Period', start: '09:00', end: '09:45', type: 'period' };
    const isEdit   = !!(this.getDaySchedule(activeDay)[assignSlot]);
    const ptype    = assignForm.type || 'class';
    const needSubj = ptype !== 'break' && ptype !== 'free';

    const selectedSubIds = Array.isArray(assignForm.subjectIds)
      ? assignForm.subjectIds.map(String)
      : (assignForm.subjectId ? [String(assignForm.subjectId)] : []);

    let filteredTeachers = [];

    if (selectedSubIds.length > 0) {
      filteredTeachers = (this.state.teachers || []).filter((teacher) => {
        if (!teacher.subject) return false;
        const teacherTokens = Array.isArray(teacher.subject)
          ? teacher.subject.map(s => String(s).trim().toLowerCase())
          : String(teacher.subject).split(',').map(s => s.trim().toLowerCase());

        return teacherTokens.some(tToken => {
          return selectedSubIds.some(selId => {
            if (tToken === String(selId).toLowerCase()) return true;
            const subObj = (this.state.subjects || []).find(s => String(s.id) === String(selId));
            if (subObj && subObj.name && tToken === String(subObj.name).trim().toLowerCase()) return true;
            return false;
          });
        });
      });
    } else {
      filteredTeachers = [];
    }

    const canSave  = !needSubj || !!assignForm.subjectId || (Array.isArray(assignForm.subjectIds) && assignForm.subjectIds.length > 0);

    return (
      <Modal show={showAssignModal} onHide={this.closeAssign} centered className="ttb-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="modal-title-icon" style={{ background: '#e8f4fe', color: '#36a1f5' }}>
              <i className="fal fa-book-open" />
            </span>
            {isEdit ? 'Edit Period' : 'Add Class Period'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={this.saveAssign}>

            {/* ── Period Type ────────────────────────────────────────────── */}
            <div className="ttb-modal-section">
              <div className="ttb-modal-section-label">Period Type</div>
              <div className="period-type-grid">
                {PERIOD_TYPES.map(({ key, icon, label, sub }) => (
                  <button key={key} type="button"
                    className={`type-card ${key} ${ptype === key ? 'selected' : ''}`}
                    onClick={() => this.setForm('type', key)}>
                    <div className="tc-icon-wrap"><i className={icon} /></div>
                    <span className="tc-label">{label}</span>
                    <span className="tc-sub">{sub}</span>
                    <span className="tc-check"><i className="fas fa-check" /></span>
                  </button>
                ))}
              </div>
            </div>

            {/* ── Time Range ─────────────────────────────────────────────── */}
            <div className="ttb-modal-section">
              <div className="ttb-modal-section-label">Time Range</div>
              <div className="ttb-time-row">
                <Form.Group style={{ marginBottom: 0 }}>
                  <label className="ttb-form-label">Start Time *</label>
                  <Form.Control type="time"
                    value={assignForm.startTime || bell.start}
                    onChange={e => this.setForm('startTime', e.target.value)}
                    className="ttb-form-input" />
                </Form.Group>
                <div className="time-sep"><i className="fal fa-arrow-right" /></div>
                <Form.Group style={{ marginBottom: 0 }}>
                  <label className="ttb-form-label">End Time *</label>
                  <Form.Control type="time"
                    value={assignForm.endTime || bell.end}
                    onChange={e => this.setForm('endTime', e.target.value)}
                    className="ttb-form-input" />
                </Form.Group>
              </div>
            </div>

            {/* ── Subject & Teacher Selection ───────────────────────────── */}
            {needSubj && (
              <div className="ttb-modal-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div className="ttb-modal-section-label" style={{ marginBottom: 0 }}>Subject & Lecturer</div>
                  <button
                    type="button" className="ttb-btn ghost sm"
                    onClick={() => this.setState(s => ({ showSubjectManager: !s.showSubjectManager }))}
                  >
                    <i className="fal fa-sliders-h" style={{ marginRight: 4 }} />
                    {this.state.showSubjectManager ? 'Hide Subjects' : 'Manage Subjects'}
                  </button>
                </div>

                {/* Inline subject manager */}
                {this.state.showSubjectManager && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: 8 }}>
                      Subject Catalog ({this.state.subjects.length} subjects)
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                      {this.state.subjects.map(s => (
                        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 4, padding: '3px 8px', fontSize: 12 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
                          <span style={{ fontWeight: 600 }}>{s.name}</span>
                          <span style={{ color: '#94a3b8', fontSize: 10 }}>({s.code})</span>
                        </div>
                      ))}
                    </div>

                    {/* Add new subject row */}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div style={{ flex: '2 1 120px' }}>
                        <label style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', display: 'block', marginBottom: 3 }}>NAME *</label>
                        <input
                          type="text" className="ttb-form-input" placeholder="e.g. Mathematics"
                          value={this.state.newSubjectName}
                          onChange={e => this.setState({ newSubjectName: e.target.value })}
                          style={{ height: 32, fontSize: 12 }}
                        />
                      </div>
                      <div style={{ flex: '1 1 70px' }}>
                        <label style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', display: 'block', marginBottom: 3 }}>SHORT</label>
                        <input
                          type="text" className="ttb-form-input" placeholder="Maths"
                          value={this.state.newSubjectShort}
                          onChange={e => this.setState({ newSubjectShort: e.target.value })}
                          style={{ height: 32, fontSize: 12 }}
                        />
                      </div>
                      <div style={{ flex: '1 1 80px' }}>
                        <label style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', display: 'block', marginBottom: 3 }}>CODE</label>
                        <input
                          type="text" className="ttb-form-input" placeholder="MAT-101"
                          value={this.state.newSubjectCode}
                          onChange={e => this.setState({ newSubjectCode: e.target.value })}
                          style={{ height: 32, fontSize: 12 }}
                        />
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        <label style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', display: 'block', marginBottom: 3 }}>COLOR</label>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 140 }}>
                          {SUBJECT_COLORS.slice(0, 9).map(c => (
                            <div
                              key={c}
                              onClick={() => this.setState({ newSubjectColor: c })}
                              style={{
                                width: 18, height: 18, borderRadius: 4, background: c,
                                cursor: 'pointer', flexShrink: 0,
                                outline: this.state.newSubjectColor === c ? `2px solid ${c}` : 'none',
                                outlineOffset: 2,
                              }}
                            />
                          ))}
                        </div>
                      </div>
                      <button
                        type="button" className="ttb-btn primary"
                        style={{ padding: '6px 14px', fontSize: 12, alignSelf: 'flex-end', flexShrink: 0 }}
                        onClick={this.addSubject}
                        disabled={!this.state.newSubjectName.trim()}
                      >
                        <i className="fal fa-plus" style={{ marginRight: 4 }} />Add
                      </button>
                    </div>
                  </div>
                )}

                <Row>
                  <Col md={6} style={{ marginBottom: 0 }}>
                    <Form.Group style={{ marginBottom: 0 }}>
                      <label className="ttb-form-label">Subject(s) *</label>
                      <CheckboxDropdown
                        options={(this.state.subjects || []).map(s => ({ value: String(s.id), label: `${s.name} (${s.code})` }))}
                        value={assignForm.subjectIds ? assignForm.subjectIds.map(String) : (assignForm.subjectId ? [String(assignForm.subjectId)] : [])}
                        onChange={(selectedValues) => {
                          const numIds = selectedValues.map(v => parseInt(v)).filter(Boolean);

                          // Filter teacher selection to keep only teachers assigned to the new subjects
                          const newTeacherIds = (assignForm.teacherIds || []).filter(tId => {
                            const tObj = (this.state.teachers || []).find(t => String(t.id) === String(tId));
                            if (!tObj || !tObj.subject) return false;
                            const tTokens = String(tObj.subject).split(',').map(s => s.trim().toLowerCase());
                            return tTokens.some(tToken => {
                              return numIds.some(selId => {
                                if (tToken === String(selId).toLowerCase()) return true;
                                const sObj = (this.state.subjects || []).find(s => String(s.id) === String(selId));
                                if (sObj && sObj.name && tToken === String(sObj.name).trim().toLowerCase()) return true;
                                return false;
                              });
                            });
                          });

                          this.setState(s => ({
                            assignForm: {
                              ...s.assignForm,
                              subjectIds: numIds,
                              subjectId: numIds[0] || '',
                              teacherIds: newTeacherIds,
                              teacherId: newTeacherIds[0] || ''
                            }
                          }));
                        }}
                        placeholder="Select Subject(s)"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group style={{ marginBottom: 0 }}>
                      <label className="ttb-form-label">Lecturer(s)</label>
                      <CheckboxDropdown
                        options={filteredTeachers.map(t => ({ value: String(t.id), label: t.name }))}
                        value={assignForm.teacherIds ? assignForm.teacherIds.map(String) : (assignForm.teacherId ? [String(assignForm.teacherId)] : [])}
                        onChange={(selectedValues) => {
                          const numIds = selectedValues.map(v => parseInt(v)).filter(Boolean);
                          this.setState(s => ({
                            assignForm: {
                              ...s.assignForm,
                              teacherIds: numIds,
                              teacherId: numIds[0] || ''
                            }
                          }));
                        }}
                        placeholder={selectedSubIds.length > 0 ? "Select Lecturer(s)" : "Select Subject First"}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}

            {/* ── Assignment ─────────────────────────────────────────────── */}
            <div className="ttb-modal-section">
              <div className="ttb-modal-section-label">Assignment</div>
              <Row>
                <Col md={4}>
                  <Form.Group style={{ marginBottom: 0 }}>
                    <label className="ttb-form-label">Section</label>
                    <Form.Control as="select"
                      value={selectedSection}
                      onChange={e => {
                        const sectionId = this.state.sectionIdMap[`${this.state.selectedClassId}||${e.target.value}`] || null;
                        this.setState({ selectedSection: e.target.value, selectedSectionId: sectionId });
                      }}
                      className="ttb-form-input">
                      {this.getClassObj().sections.map(s => (
                        <option key={s} value={s}>Sec-{s}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group style={{ marginBottom: 0 }}>
                    <label className="ttb-form-label">Class / Year</label>
                    <Form.Control as="select"
                      value={selectedClass}
                      onChange={e => {
                        const cls = this.state.classesList.find(c => c.label === e.target.value) || this.state.classesList[0];
                        const firstSec  = cls?.sections[0] || '';
                        const classId   = parseInt(cls?.id);
                        const sectionId = this.state.sectionIdMap[`${classId}||${firstSec}`] || null;
                        this.setState({ selectedClass: e.target.value, selectedClassId: classId, selectedSection: firstSec, selectedSectionId: sectionId });
                      }}
                      className="ttb-form-input">
                      {this.state.classesList.map(c => (
                        <option key={c.id} value={c.label}>{c.label}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group style={{ marginBottom: 0 }}>
                    <label className="ttb-form-label">Room / Location</label>
                    <Form.Control as="select"
                      value={assignForm.room}
                      onChange={e => this.setForm('room', e.target.value)}
                      className="ttb-form-input">
                      {ROOMS.map(r => <option key={r}>{r}</option>)}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* ── Additional Notes ───────────────────────────────────────── */}
            <div className="ttb-modal-section bg-soft">
              <div className="ttb-modal-section-label">Additional Notes</div>
              <Form.Control as="textarea" rows={2}
                value={assignForm.note || ''}
                onChange={e => this.setForm('note', e.target.value)}
                placeholder="e.g. Bring textbook, Chapter 5 quiz…"
                className="ttb-form-input"
                style={{ resize: 'none' }} />
            </div>

            <div className="ttb-modal-footer">
              <button type="button" className="ttb-btn outline" onClick={this.closeAssign}>
                Cancel
              </button>
              <button type="submit" className="ttb-btn primary"
                disabled={!canSave}
                style={{ opacity: canSave ? 1 : 0.45 }}>
                <i className={`fal fa-${isEdit ? 'save' : 'check'}`} style={{ marginRight: 6 }} />
                {isEdit ? 'Save Changes' : 'Add Period'}
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    );
  }

  // ─── Render: clash warning modal ─────────────────────────────────────────
  renderClashModal() {
    const { clashWarning } = this.state;
    if (!clashWarning || !clashWarning.length) return null;
    return (
      <Modal show centered className="ttb-modal"
        onHide={() => this.setState({ clashWarning: null, pendingAssign: null })}>
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="modal-title-icon" style={{ background: '#fbeeea', color: '#c0533a' }}>
              <i className="fal fa-exclamation-triangle" />
            </span>
            Scheduling Conflict Detected
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ttb-clash-list">
            {clashWarning.map((clash, i) => (
              <div key={i} className="clash-item" style={{ borderLeftColor: clash.color }}>
                <div className="clash-icon" style={{ color: clash.color, background: `${clash.color}14` }}>
                  <i className={clash.icon} />
                </div>
                <div className="clash-body">
                  <div className="clash-title" style={{ color: clash.color }}>{clash.title}</div>
                  <div className="clash-desc">{clash.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="ttb-clash-note">
            <i className="fal fa-info-circle" />
            You can go back to change the assignment, or save anyway to override.
          </div>
          <div className="ttb-modal-footer">
            <button type="button" className="ttb-btn outline"
              onClick={() => this.setState({ clashWarning: null, pendingAssign: null })}>
              <i className="fal fa-arrow-left" style={{ marginRight: 6 }} />Go Back
            </button>
            <button type="button" className="ttb-btn danger" onClick={this.forceAssign}>
              <i className="fal fa-save" style={{ marginRight: 6 }} />Save Anyway
            </button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  // ─── Render: bell config modal ────────────────────────────────────────────
  renderBellConfigModal() {
    const { showBellConfigModal, bellConfigDraft, selectedClass, selectedSection } = this.state;
    if (!showBellConfigModal || !bellConfigDraft) return null;

    const typeIcon = (bell) => {
      if (bell.type === 'period')  return <span className="bct-pnum">P{bell.num}</span>;
      if (bell.type === 'special') return <i className="fal fa-hands" title="Assembly" />;
      if (bell.slot === 'lunch')   return <i className="fal fa-utensils" title="Lunch" />;
      return <i className="fal fa-coffee" title="Break" />;
    };

    return (
      <Modal show={showBellConfigModal} onHide={this.closeBellConfig} centered className="ttb-modal ttb-modal-bell">
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="modal-title-icon" style={{ background: '#fdf3e0', color: '#d97706' }}>
              <i className="fal fa-bell" />
            </span>
            Bell Schedule Configuration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          <div style={{ padding: '12px 24px 8px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: 6, letterSpacing: '0.5px' }}>
              Customization Target Scope
            </div>
            <div style={{ display: 'flex', gap: 18, fontSize: 12.5, fontWeight: 600, color: '#334155' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="bellScope"
                  value="section"
                  checked={(this.state.bellScope || 'section') === 'section'}
                  onChange={() => this.setState({ bellScope: 'section' })}
                />
                <span>This Section ({selectedClass} Sec-{selectedSection})</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="bellScope"
                  value="class"
                  checked={this.state.bellScope === 'class'}
                  onChange={() => this.setState({ bellScope: 'class' })}
                />
                <span>Entire Class ({selectedClass})</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="bellScope"
                  value="branch"
                  checked={this.state.bellScope === 'branch'}
                  onChange={() => this.setState({ bellScope: 'branch' })}
                />
                <span>Branch Default</span>
              </label>
            </div>
          </div>
          <p style={{ fontSize: 12, color: '#6b6b78', padding: '10px 24px 6px', margin: 0 }}>
            Add, remove, or reorder slots and breaks below. Click <strong>Apply Schedule</strong> to persist changes to your selected scope.
          </p>

          {/* Table */}
          <div className="bell-body-scroll">
            <table className="bell-config-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  <th style={{ width: 48 }}>#</th>
                  <th>Label</th>
                  <th style={{ width: 110 }}>Start</th>
                  <th style={{ width: 110 }}>End</th>
                  <th style={{ width: 80, textAlign: 'center' }}>Move</th>
                  <th style={{ width: 48 }} />
                </tr>
              </thead>
              <tbody>
                {bellConfigDraft.map((bell, idx) => (
                  <tr
                    key={bell.slot}
                    className={bell.type !== 'period' ? 'bct-break-row' : ''}
                    style={{ verticalAlign: 'middle' }}
                  >
                    {/* Type icon */}
                    <td className="bct-slot" style={{ textAlign: 'center' }}>
                      {typeIcon(bell)}
                    </td>

                    {/* Type badge */}
                    <td>
                      <span style={{
                        display: 'inline-block',
                        fontSize: 10, fontWeight: 700,
                        borderRadius: 4, padding: '2px 6px',
                        background: bell.type === 'period' ? '#eef2ff' : bell.type === 'special' ? '#fff7ed' : '#f0fdf4',
                        color:      bell.type === 'period' ? '#6366f1' : bell.type === 'special' ? '#d97706' : '#15803d',
                        textTransform: 'uppercase',
                      }}>
                        {bell.type === 'special' ? 'asm' : bell.type === 'break' ? 'brk' : 'prd'}
                      </span>
                    </td>

                    {/* Label — editable for all types */}
                    <td>
                      <input
                        type="text"
                        className="ttb-form-input bct-label-input"
                        value={bell.label}
                        onChange={e => this.setBellDraft(bell.slot, 'label', e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </td>

                    {/* Start */}
                    <td>
                      <input
                        type="time"
                        className="ttb-form-input bct-time-input"
                        value={bell.start}
                        onChange={e => this.setBellDraft(bell.slot, 'start', e.target.value)}
                      />
                    </td>

                    {/* End */}
                    <td>
                      <input
                        type="time"
                        className="ttb-form-input bct-time-input"
                        value={bell.end}
                        onChange={e => this.setBellDraft(bell.slot, 'end', e.target.value)}
                      />
                    </td>

                    {/* Move up / down */}
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <button
                          type="button"
                          className="ttb-btn ghost sm"
                          style={{ padding: '2px 6px', minWidth: 0, opacity: idx === 0 ? 0.3 : 1 }}
                          disabled={idx === 0}
                          onClick={() => this.moveBellRow(bell.slot, -1)}
                          title="Move up"
                        >
                          <i className="fal fa-chevron-up" />
                        </button>
                        <button
                          type="button"
                          className="ttb-btn ghost sm"
                          style={{ padding: '2px 6px', minWidth: 0, opacity: idx === bellConfigDraft.length - 1 ? 0.3 : 1 }}
                          disabled={idx === bellConfigDraft.length - 1}
                          onClick={() => this.moveBellRow(bell.slot, 1)}
                          title="Move down"
                        >
                          <i className="fal fa-chevron-down" />
                        </button>
                      </div>
                    </td>

                    {/* Delete */}
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        title="Remove this slot"
                        style={{
                          background: 'none', border: 'none',
                          color: '#ef4444', cursor: 'pointer',
                          fontSize: 14, padding: '2px 6px',
                          borderRadius: 4,
                          opacity: bellConfigDraft.length <= 1 ? 0.3 : 1,
                        }}
                        disabled={bellConfigDraft.length <= 1}
                        onClick={() => this.deleteBellRow(bell.slot)}
                      >
                        <i className="fal fa-trash-alt" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add row buttons */}
          <div style={{ display: 'flex', gap: 8, padding: '10px 24px 4px' }}>
            <button
              type="button"
              className="ttb-btn ghost sm"
              onClick={this.addBellPeriod}
              style={{ fontSize: 12 }}
            >
              <i className="fal fa-plus" style={{ marginRight: 5 }} />Add Period
            </button>
            <button
              type="button"
              className="ttb-btn ghost sm"
              onClick={this.addBellBreak}
              style={{ fontSize: 12 }}
            >
              <i className="fal fa-coffee" style={{ marginRight: 5 }} />Add Break
            </button>
          </div>

          {this.state.bellError && (
            <div className="ttb-modal-error" style={{ color: '#dc2626', background: '#fee2e2', border: '1px solid #fca5a5', padding: '8px 12px', borderRadius: 6, fontSize: 12, margin: '8px 24px 0' }}>
              <i className="fal fa-exclamation-circle" style={{ marginRight: 6 }} />
              {this.state.bellError}
            </div>
          )}

          <div className="ttb-modal-footer" style={{ marginTop: 8 }}>
            <button type="button" className="ttb-btn ghost sm" onClick={this.resetBellConfig} disabled={this.state.bellLoading}>
              <i className="fal fa-undo" style={{ marginRight: 5 }} />Reset to Default
            </button>
            <div style={{ flex: 1 }} />
            <button type="button" className="ttb-btn outline" onClick={this.closeBellConfig} disabled={this.state.bellLoading}>Cancel</button>
            <button type="button" className="ttb-btn primary" onClick={this.saveBellConfig} disabled={this.state.bellLoading} style={{ background: 'linear-gradient(135deg, #36a1f5, #1e8de0)', borderColor: '#36a1f5', fontWeight: 700 }}>
              {this.state.bellLoading ? <><i className="fal fa-spinner fa-spin mr-1" />Saving Timings...</> : <><i className="fal fa-check" style={{ marginRight: 6 }} />Apply Schedule</>}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  // ─── Render: print view (hidden on screen, visible only @media print) ─────
  renderPrintView() {
    const { selectedBranch, selectedClass, selectedSection } = this.state;
    const bellSch = this.getBellSchedule().filter(b => b.type === 'period');
    return (
      <div className="ttb-print-view">
        <div className="pv-header">
          <h2>{selectedClass} · Section {selectedSection}</h2>
          <p>{selectedBranch} &mdash; Weekly Timetable &mdash; Printed {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <table className="pv-table">
          <thead>
            <tr>
              <th>Period</th>
              <th>Time</th>
              {[1,2,3,4,5,6].map(d => <th key={d}>{DAYS[d]}</th>)}
            </tr>
          </thead>
          <tbody>
            {bellSch.map(bell => (
              <tr key={bell.slot}>
                <td className="pv-td-period">P{bell.num}</td>
                <td className="pv-td-time">{bell.start}–{bell.end}</td>
                {[1,2,3,4,5,6].map(d => {
                  const entry = this.getDaySchedule(d)[bell.slot];
                  const subj  = entry ? getSubject(entry.subjectId) : null;
                  const tch   = entry?.teacherId ? this.getTeacher(entry.teacherId) : null;
                  return (
                    <td key={d} className="pv-td-cell" style={subj ? { borderTop: `3px solid ${subj.color}` } : {}}>
                      {subj ? (
                        <>
                          <div className="pv-subj">{subj.short}</div>
                          {tch && <div className="pv-tch">{tch.initials}</div>}
                          {entry.room !== 'Own Classroom' && <div className="pv-room">{entry.room}</div>}
                        </>
                      ) : <span className="pv-empty">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pv-footer">
          <span>Generated by Rizee College ERP</span>
          <span>Confidential — For Internal Use Only</span>
        </div>
      </div>
    );
  }

  // ─── Render: copy modal ───────────────────────────────────────────────────
  renderCopyModal() {
    const { showCopyModal, copyMode, copyTargetDays, activeDay,
            copyTargetBranch, copyTargetClass, copyTargetSection,
            copyDateFrom, copyDateTo, configDbId } = this.state;
    const targetCls  = this.getClassObj(copyTargetClass);
    const dowCounts  = weekdayCounts(copyDateFrom, copyDateTo);
    const totalDays  = Object.values(dowCounts).reduce((a, b) => a + b, 0);
    const dateRangeOk = copyDateFrom && copyDateTo && copyDateFrom <= copyDateTo;

    return (
      <Modal show={showCopyModal} onHide={this.closeCopyModal} centered className="ttb-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="modal-title-icon" style={{ background: '#e8f4fe', color: '#36a1f5' }}><i className="fal fa-copy" /></span>
            Copy / Replicate Timetable
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ttb-modal-section">
            <div className="copy-mode-tabs">
              <button className={`cmt-tab ${copyMode === 'day' ? 'active' : ''}`} onClick={() => this.setState({ copyMode: 'day' })}>
                <i className="fal fa-calendar-day" /> Copy Day
              </button>
              <button className={`cmt-tab ${copyMode === 'section' ? 'active' : ''}`} onClick={() => this.setState({ copyMode: 'section' })}>
                <i className="fal fa-users" /> Copy to Section
              </button>
              <button className={`cmt-tab ${copyMode === 'dateRange' ? 'active' : ''}`} onClick={() => this.setState({ copyMode: 'dateRange' })}>
                <i className="fal fa-calendar-alt" /> Date Range
              </button>
            </div>

            {/* ── Copy day to days ────────────────────────────────── */}
            {copyMode === 'day' && (
              <>
                <div className="ttb-modal-section-label">Copy <strong>{DAYS[activeDay]}</strong> schedule to</div>
                <div className="copy-day-checkboxes">
                  {[1,2,3,4,5,6].map(d => {
                    const isSrc = d === activeDay;
                    const chk   = copyTargetDays.includes(d);
                    return (
                      <label key={d} className={`cdc-item ${chk ? 'checked' : ''} ${isSrc ? 'disabled' : ''}`}>
                        <input type="checkbox" checked={chk} disabled={isSrc}
                          onChange={() => !isSrc && this.toggleCopyDay(d)} />
                        <span>{DAYS[d]}</span>
                      </label>
                    );
                  })}
                </div>
                <p style={{ fontSize: 11.5, color: '#a8a8b4', marginTop: 10, marginBottom: 0 }}>
                  <i className="fal fa-info-circle" style={{ marginRight: 4 }} />Replaces target day's existing schedule.
                </p>
              </>
            )}

            {/* ── Copy week to section ────────────────────────────── */}
            {copyMode === 'section' && (
              <>
                <div className="ttb-modal-section-label">Destination — all 6 days will be copied</div>
                <Row>
                  <Col md={4}>
                    <Form.Group style={{ marginBottom: 10 }}>
                      <label className="ttb-form-label">Branch</label>
                      <Form.Control as="select" value={copyTargetBranch}
                        onChange={e => this.setState({ copyTargetBranch: e.target.value })} className="ttb-form-input">
                        {this.state.branches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group style={{ marginBottom: 10 }}>
                      <label className="ttb-form-label">Class</label>
                      <Form.Control as="select" value={copyTargetClass}
                        onChange={e => {
                          const cls = this.state.classesList.find(c => c.label === e.target.value) || this.state.classesList[0];
                          this.setState({ copyTargetClass: e.target.value, copyTargetSection: cls?.sections[0] || '' });
                        }} className="ttb-form-input">
                        {this.state.classesList.map(c => <option key={c.id} value={c.label}>{c.label}</option>)}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group style={{ marginBottom: 10 }}>
                      <label className="ttb-form-label">Section</label>
                      <Form.Control as="select" value={copyTargetSection}
                        onChange={e => this.setState({ copyTargetSection: e.target.value })} className="ttb-form-input">
                        {targetCls.sections.map(s => <option key={s} value={s}>Sec-{s}</option>)}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}

            {/* ── Date range ──────────────────────────────────────── */}
            {copyMode === 'dateRange' && (
              <>
                <div className="ttb-modal-section-label">
                  Set effective date range for this timetable
                  {configDbId && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#3d8a55', fontWeight: 500 }}>
                      <i className="fal fa-check-circle" style={{ marginRight: 3 }} />Saved
                    </span>
                  )}
                </div>
                <Row style={{ marginBottom: 12 }}>
                  <Col md={6}>
                    <Form.Group style={{ marginBottom: 0 }}>
                      <label className="ttb-form-label">From Date</label>
                      <Form.Control type="date" value={copyDateFrom}
                        onChange={e => this.setState({ copyDateFrom: e.target.value })}
                        className="ttb-form-input" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group style={{ marginBottom: 0 }}>
                      <label className="ttb-form-label">To Date</label>
                      <Form.Control type="date" value={copyDateTo}
                        min={copyDateFrom}
                        onChange={e => this.setState({ copyDateTo: e.target.value })}
                        className="ttb-form-input" />
                    </Form.Group>
                  </Col>
                </Row>

                {dateRangeOk && (
                  <div style={{ background: '#f0f4ff', border: '1px solid #d5e0ff', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#3b5bdb', marginBottom: 8 }}>
                      <i className="fal fa-calendar-check" style={{ marginRight: 6 }} />
                      {totalDays} school days across this range
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {[1,2,3,4,5,6].map(d => dowCounts[d] > 0 && (
                        <span key={d} style={{ fontSize: 11, background: '#fff', border: '1px solid #c5d3ff', borderRadius: 5, padding: '2px 8px', color: '#3b5bdb' }}>
                          {DAYS[d]} × {dowCounts[d]}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {copyDateFrom && copyDateTo && copyDateFrom > copyDateTo && (
                  <p style={{ color: '#dc2626', fontSize: 12, marginTop: 8, marginBottom: 0 }}>
                    <i className="fal fa-exclamation-circle" style={{ marginRight: 4 }} />
                    End date must be after start date.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="ttb-modal-footer">
            <button type="button" className="ttb-btn outline" onClick={this.closeCopyModal}>Cancel</button>
            {copyMode === 'day' && (
              <button type="button" className="ttb-btn primary"
                disabled={!copyTargetDays.length} style={{ opacity: copyTargetDays.length ? 1 : 0.5 }}
                onClick={this.copyDayToTargets}>
                <i className="fal fa-copy" style={{ marginRight: 6 }} />Copy to {copyTargetDays.length || '—'} days
              </button>
            )}
            {copyMode === 'section' && (
              <button type="button" className="ttb-btn primary" onClick={this.copyWeekToSection}>
                <i className="fal fa-copy" style={{ marginRight: 6 }} />Copy → {copyTargetClass} Sec-{copyTargetSection}
              </button>
            )}
            {copyMode === 'dateRange' && (
              <button type="button" className="ttb-btn primary"
                disabled={!dateRangeOk} style={{ opacity: dateRangeOk ? 1 : 0.5 }}
                onClick={this.saveDateRange}>
                <i className="fal fa-save" style={{ marginRight: 6 }} />
                Save Date Range
              </button>
            )}
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  renderSaveTemplateModal() {
    const { showSaveTemplateModal, templateNameInput, selectedClass, selectedSection } = this.state;
    return (
      <Modal show={showSaveTemplateModal} onHide={() => this.setState({ showSaveTemplateModal: false })} centered size="sm" className="ttb-modal">
        <Modal.Header closeButton>
          <Modal.Title><span className="modal-title-icon" style={{ background: '#e8f3ec', color: '#3d8a55' }}><i className="fal fa-bookmark" /></span>Save as Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ttb-modal-section">
            <div className="ttb-modal-section-label">Template Name</div>
            <Form.Control type="text" value={templateNameInput}
              onChange={e => this.setState({ templateNameInput: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && this.saveTemplate()}
              placeholder={`${selectedClass} Sec-${selectedSection} Week`}
              className="ttb-form-input" autoFocus />
          </div>
          <div className="ttb-modal-footer">
            <button type="button" className="ttb-btn outline" onClick={() => this.setState({ showSaveTemplateModal: false })}>Cancel</button>
            <button type="button" className="ttb-btn success" disabled={!templateNameInput.trim()}
              style={{ opacity: templateNameInput.trim() ? 1 : 0.5 }} onClick={this.saveTemplate}>
              <i className="fal fa-bookmark" style={{ marginRight: 6 }} />Save
            </button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  renderLoadTemplateModal() {
    const { showLoadTemplateModal } = this.state;
    const templates = this.state.templates || [];
    return (
      <Modal show={showLoadTemplateModal} onHide={() => this.setState({ showLoadTemplateModal: false })} centered className="ttb-modal">
        <Modal.Header closeButton>
          <Modal.Title><span className="modal-title-icon" style={{ background: '#f0ebfb', color: '#7c4dcc' }}><i className="fal fa-layer-group" /></span>Apply a Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ttb-modal-section" style={{ padding: 0 }}>
            {templates.length === 0 ? (
              <div style={{ padding: '36px 24px', textAlign: 'center', color: '#a8a8b4', fontSize: 13 }}>
                <i className="fal fa-inbox" style={{ fontSize: 32, display: 'block', marginBottom: 12 }} />No templates saved yet.
              </div>
            ) : (
              <div className="ttb-template-list">
                {templates.map(tpl => (
                  <div key={tpl.id} className="tpl-item">
                    <div className="tpl-icon"><i className="fal fa-calendar-week" /></div>
                    <div className="tpl-info">
                      <div className="tpl-name">{tpl.name}</div>
                      <div className="tpl-meta">{tpl.context} · {tpl.savedAt}</div>
                    </div>
                    <div className="tpl-actions">
                      <button className="ttb-btn success sm" onClick={() => this.applyTemplate(tpl)}><i className="fal fa-check" />Apply</button>
                      <button className="ttb-icon-btn delete" onClick={() => this.deleteTemplate(tpl.id)}><i className="fal fa-trash" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="ttb-modal-footer">
            <button type="button" className="ttb-btn outline" onClick={() => this.setState({ showLoadTemplateModal: false })}>Close</button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  // ─── Substitution modal handlers & render ─────────────────────────────────
  openSubstituteModal = (slotId, entry, bell) => {
    if (!this.guard.requireEdit()) return;
    const targetDate = addDays(this.state.weekStart, this.state.activeDay - 1);
    const dateStr = fmtDate(targetDate);
    const displayDate = fmtFull(targetDate);
    const numericSlotId = (bell && bell.id) ? parseInt(bell.id) : (parseInt(slotId) || 0);

    let subjs = [];
    if (entry) {
      if (Array.isArray(entry.subjectIds) && entry.subjectIds.length > 0) {
        subjs = entry.subjectIds.map(sid => getSubject(sid)).filter(Boolean);
      } else if (entry.subjectId) {
        const s = getSubject(entry.subjectId);
        if (s) subjs = [s];
      }
    }

    let tchs = [];
    if (entry) {
      if (Array.isArray(entry.teacherIds) && entry.teacherIds.length > 0) {
        tchs = entry.teacherIds.map(tid => this.getTeacher(tid)).filter(Boolean);
      } else if (entry.teacherId) {
        const t = this.getTeacher(entry.teacherId);
        if (t) tchs = [t];
      }
    }
    const origTeacher = tchs[0] || null;

    this.setState({
      showSubstituteModal: true,
      substituteTargetSlot: {
        slotId,
        numericSlotId,
        displayDate,
        dateStr,
        dayOfWeek: this.state.activeDay,
        subjectName: subjs.map(s => s.name).join(' / ') || 'Class Period',
        originalTeacherId: origTeacher ? origTeacher.id : null,
        originalTeacherName: origTeacher ? origTeacher.name : 'Unassigned',
        timetableId: this.state.timetableDbId || 0,
      },
      selectedSubstituteTeacherId: '',
      selectedSubstituteSubjectId: entry?.subjectId ? String(entry.subjectId) : '',
      substituteReason: '',
      substituteError: '',
    });
  };

  submitSubstitution = async () => {
    const { substituteTargetSlot, selectedSubstituteTeacherId, selectedSubstituteSubjectId, substituteReason, substituteMap, teachers } = this.state;
    if (!selectedSubstituteTeacherId) {
      this.setState({ substituteError: 'Please select a substitute teacher.' });
      return;
    }
    this.setState({ substituteLoading: true, substituteError: '' });
    try {
      const institute_id = parseInt(Cookies.get('institutionid'));
      const sendSlotId = parseInt(substituteTargetSlot.numericSlotId || substituteTargetSlot.slotId) || 0;
      await this.props.client.mutate({
        mutation: SAVE_TIMETABLE_SUBSTITUTION,
        variables: {
          params: {
            institute_id,
            timetable_id: substituteTargetSlot.timetableId || 0,
            substitution_date: substituteTargetSlot.dateStr,
            day_of_week: substituteTargetSlot.dayOfWeek,
            slot_id: sendSlotId,
            original_teacher_id: substituteTargetSlot.originalTeacherId ? parseInt(substituteTargetSlot.originalTeacherId) : null,
            substitute_id: parseInt(selectedSubstituteTeacherId),
            subject_id: selectedSubstituteSubjectId ? parseInt(selectedSubstituteSubjectId) : null,
            reason: substituteReason || null,
            status: 'approved',
          }
        }
      });
      const subKey = `${substituteTargetSlot.dateStr}_${substituteTargetSlot.slotId}`;
      const subKeyNumeric = `${substituteTargetSlot.dateStr}_${sendSlotId}`;
      const subTch = teachers.find(t => String(t.id) === String(selectedSubstituteTeacherId));
      const subName = subTch ? subTch.name : 'Substitute';

      const newSubData = {
        substitute_id: selectedSubstituteTeacherId,
        substitute_name: subName,
        original_teacher_name: substituteTargetSlot.originalTeacherName,
        subject_id: selectedSubstituteSubjectId ? parseInt(selectedSubstituteSubjectId) : null,
        reason: substituteReason
      };

      this.setState({
        substituteLoading: false,
        showSubstituteModal: false,
        substituteMap: {
          ...substituteMap,
          [subKey]: newSubData,
          [subKeyNumeric]: newSubData
        }
      });
    } catch (err) {
      console.error('saveTimetableSubstitution failed', err);
      this.setState({ substituteLoading: false, substituteError: 'Failed to save substitution. Please try again.' });
    }
  };

  renderSubstituteModal() {
    const { showSubstituteModal, substituteTargetSlot, selectedSubstituteTeacherId, selectedSubstituteSubjectId,
            substituteReason, substituteLoading, substituteError, teachers, subjects } = this.state;
    if (!showSubstituteModal || !substituteTargetSlot) return null;

    const allSubjs = subjects || DEFAULT_SUBJECTS;
    const selectedSubjObj = allSubjs.find(s => String(s.id) === String(selectedSubstituteSubjectId));

    // Filter teachers dynamically based on selected subject
    const rawFiltered = teachers.filter(t => {
      if (String(t.id) === String(substituteTargetSlot.originalTeacherId)) return false;
      if (!selectedSubstituteSubjectId) return true;
      const tSubj = (t.subject || '').toLowerCase();
      const subjName = (selectedSubjObj?.name || '').toLowerCase();
      const subjCode = (selectedSubjObj?.code || '').toLowerCase();
      const subjId = String(selectedSubstituteSubjectId);
      return String(t.subject_id) === subjId || tSubj.includes(subjName) || tSubj.includes(subjCode) || tSubj.includes(subjId);
    });

    const displayTeachers = rawFiltered.length > 0
      ? rawFiltered
      : teachers.filter(t => String(t.id) !== String(substituteTargetSlot.originalTeacherId));

    const selectedTeacherObj = teachers.find(t => String(t.id) === String(selectedSubstituteTeacherId));

    return (
      <Modal show={showSubstituteModal} onHide={() => this.setState({ showSubstituteModal: false })} centered className="ttb-modal">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #f0ebfb, #e8f4fe)', borderBottom: '1px solid #d5c7f0' }}>
          <Modal.Title style={{ color: '#4c1d95', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="modal-title-icon" style={{ background: '#7c4dcc', color: '#ffffff', width: 34, height: 34, borderRadius: 8, display: 'grid', placeItems: 'center', fontSize: 16 }}>
              <i className="fal fa-exchange-alt" />
            </span>
            Teacher Substitution &amp; Subject Override
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px 24px' }}>
          {/* Target Summary Card */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12.5 }}>
              <div><span style={{ color: '#64748b', fontWeight: 600 }}>Date:</span> <strong style={{ color: '#0f172a' }}>{substituteTargetSlot.displayDate}</strong></div>
              <div><span style={{ color: '#64748b', fontWeight: 600 }}>Slot:</span> <strong style={{ color: '#0f172a' }}>Period {substituteTargetSlot.slotId}</strong></div>
              <div><span style={{ color: '#64748b', fontWeight: 600 }}>Default Subject:</span> <strong style={{ color: '#0f172a' }}>{substituteTargetSlot.subjectName}</strong></div>
              <div><span style={{ color: '#64748b', fontWeight: 600 }}>Original Teacher:</span> <strong style={{ color: '#dc2626' }}>{substituteTargetSlot.originalTeacherName}</strong></div>
            </div>
          </div>

          {/* 1. Subject Override Dropdown */}
          <Form.Group style={{ marginBottom: 14 }}>
            <label className="ttb-form-label" style={{ fontWeight: 700, fontSize: 12.5, color: '#334155' }}>
              1. Subject (Filter / Override)
            </label>
            <Form.Control
              as="select"
              value={selectedSubstituteSubjectId}
              onChange={e => this.setState({ selectedSubstituteSubjectId: e.target.value, selectedSubstituteTeacherId: '' })}
              className="ttb-form-input"
              style={{ borderRadius: 8, height: 40 }}
            >
              <option value="">— Keep Default Subject ({substituteTargetSlot.subjectName}) —</option>
              {allSubjs.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
              ))}
            </Form.Control>
            <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, display: 'block' }}>
              Selecting a subject filters qualified faculty below.
            </span>
          </Form.Group>

          {/* 2. Cascading Teacher Dropdown */}
          <Form.Group style={{ marginBottom: 14 }}>
            <label className="ttb-form-label" style={{ fontWeight: 700, fontSize: 12.5, color: '#334155' }}>
              2. Substitute Teacher * {selectedSubjObj && <span style={{ color: '#7c4dcc', fontWeight: 600 }}>({selectedSubjObj.name} Staff)</span>}
            </label>
            <Form.Control
              as="select"
              value={selectedSubstituteTeacherId}
              onChange={e => this.setState({ selectedSubstituteTeacherId: e.target.value, substituteError: '' })}
              className="ttb-form-input"
              style={{ borderRadius: 8, height: 40 }}
            >
              <option value="">— Select Qualified Substitute —</option>
              {displayTeachers.map(t => (
                <option key={t.id} value={t.id}>{t.name} {t.subject ? `(${t.subject})` : ''}</option>
              ))}
            </Form.Control>
          </Form.Group>

          {/* Live Preview Card */}
          {selectedTeacherObj && (
            <div style={{ background: '#f0ebfb', border: '1.5px solid #d5c7f0', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12 }}>
              <div style={{ color: '#6d28d9', fontWeight: 800, marginBottom: 2 }}>
                <i className="fal fa-exchange-alt mr-1" /> Live Preview:
              </div>
              <div style={{ color: '#4c1d95', fontWeight: 600 }}>
                ⇄ Substituted by: <strong>{selectedTeacherObj.name}</strong> {selectedSubjObj ? `(${selectedSubjObj.name})` : ''}
              </div>
            </div>
          )}

          {/* 3. Reason / Notes */}
          <Form.Group style={{ marginBottom: 14 }}>
            <label className="ttb-form-label" style={{ fontWeight: 700, fontSize: 12.5, color: '#334155' }}>Reason / Leave Details (optional)</label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="e.g. Leave, Medical emergency, Official duty..."
              value={substituteReason}
              onChange={e => this.setState({ substituteReason: e.target.value })}
              className="ttb-form-input"
              style={{ resize: 'none', borderRadius: 8 }}
            />
          </Form.Group>

          {substituteError && (
            <div className="ttb-modal-error" style={{ color: '#dc2626', background: '#fee2e2', border: '1px solid #fca5a5', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginTop: 8 }}>
              <i className="fal fa-exclamation-circle" style={{ marginRight: 6 }} />
              {substituteError}
            </div>
          )}

          <div className="ttb-modal-footer" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14, marginTop: 16 }}>
            <button type="button" className="ttb-btn outline" onClick={() => this.setState({ showSubstituteModal: false })} disabled={substituteLoading}>
              Cancel
            </button>
            <button type="button" className="ttb-btn primary" onClick={this.submitSubstitution} disabled={substituteLoading} style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', borderColor: '#7c3aed', padding: '8px 20px', borderRadius: 8, fontWeight: 700 }}>
              {substituteLoading ? <><i className="fal fa-spinner fa-spin mr-1" />Saving...</> : <><i className="fal fa-check mr-1" />Save Substitution</>}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  // ─── Holiday & Exam Day Handlers ──────────────────────────────────────────
  openHolidayModal = (dayNum) => {
    if (!this.guard.requireEdit()) return;
    const targetDate = addDays(this.state.weekStart, dayNum - 1);
    const dateStr = fmtDate(targetDate);
    const existing = this.state.holidays[dayNum] || null;

    this.setState({
      showHolidayModal: true,
      holidayTargetDay: dayNum,
      holidayTargetDate: dateStr,
      holidayNameInput: existing ? existing.name : '',
      holidayTypeInput: existing ? existing.type : 'school',
      holidayReasonInput: existing ? existing.reason : '',
      holidayLoading: false,
      holidayError: '',
    });
  };

  saveHoliday = async () => {
    const { holidayTargetDay, holidayTargetDate, holidayNameInput, holidayTypeInput, holidayReasonInput, holidays, selectedBranch } = this.state;
    if (!holidayNameInput.trim()) {
      this.setState({ holidayError: 'Please enter a name or title for this special day / holiday.' });
      return;
    }
    this.setState({ holidayLoading: true, holidayError: '' });
    try {
      const institute_id = parseInt(Cookies.get('institutionid'));
      const sendType = holidayTypeInput === 'exam_day' ? 'exam' : holidayTypeInput;
      const res = await this.props.client.mutate({
        mutation: SAVE_HOLIDAY,
        variables: {
          params: {
            institute_id,
            holiday_date: holidayTargetDate,
            name: holidayNameInput.trim(),
            type: sendType,
            reason: holidayReasonInput || null,
            exception_type: holidayTypeInput === 'exam_day' ? 'exam' : 'holiday',
            branch_name: selectedBranch || null,
          }
        }
      });
      const savedHol = res.data?.saveHoliday;
      this.setState({
        holidayLoading: false,
        showHolidayModal: false,
        holidays: {
          ...holidays,
          [holidayTargetDay]: {
            id: savedHol?.id || Date.now(),
            date: holidayTargetDate,
            name: holidayNameInput.trim(),
            type: holidayTypeInput,
            reason: holidayReasonInput || '',
          }
        }
      });
    } catch (err) {
      console.error('saveHoliday failed', err);
      this.setState({ holidayLoading: false, holidayError: 'Failed to save holiday. Please try again.' });
    }
  };

  renderHolidayModal() {
    const { showHolidayModal, holidayTargetDate, holidayNameInput, holidayTypeInput,
            holidayReasonInput, holidayLoading, holidayError } = this.state;
    if (!showHolidayModal) return null;

    const selTypeObj = HOLIDAY_TYPES.find(ht => ht.key === holidayTypeInput) || HOLIDAY_TYPES[0];

    return (
      <Modal show={showHolidayModal} onHide={() => this.setState({ showHolidayModal: false })} centered className="ttb-modal">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #fff3e0, #ffedd5)', borderBottom: '1px solid #fed7aa' }}>
          <Modal.Title style={{ color: '#c2410c', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="modal-title-icon" style={{ background: '#f97316', color: '#ffffff', width: 34, height: 34, borderRadius: 8, display: 'grid', placeItems: 'center', fontSize: 16 }}>
              <i className="fal fa-calendar-exclamation" />
            </span>
            Declare Holiday or Exam Day
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px 24px' }}>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
            <strong>Target Date:</strong> <span style={{ color: '#ea580c', fontWeight: 700 }}>{holidayTargetDate}</span>
          </div>

          {/* Interactive Holiday / Exam Category Tile Picker */}
          <Form.Group style={{ marginBottom: 16 }}>
            <label className="ttb-form-label" style={{ fontWeight: 700, fontSize: 12.5, color: '#334155' }}>Select Category *</label>
            <div className="hol-type-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {HOLIDAY_TYPES.map(ht => {
                const isSelected = holidayTypeInput === ht.key;
                return (
                  <div key={ht.key}
                    className={`hol-type-card ${isSelected ? 'selected' : ''}`}
                    style={{
                      background: isSelected ? ht.bg : '#ffffff',
                      borderColor: isSelected ? ht.color : '#e2e8f0',
                      color: isSelected ? ht.color : '#64748b',
                      padding: '10px 8px',
                      borderRadius: 8,
                      border: `1.5px solid ${isSelected ? ht.color : '#e2e8f0'}`,
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.15s ease'
                    }}
                    onClick={() => this.setState({ holidayTypeInput: ht.key })}
                  >
                    <i className={ht.icon} style={{ fontSize: 18, marginBottom: 4, display: 'block' }} />
                    <span style={{ fontSize: 11.5, fontWeight: 700 }}>{ht.label}</span>
                  </div>
                );
              })}
            </div>
          </Form.Group>

          <Form.Group style={{ marginBottom: 14 }}>
            <label className="ttb-form-label" style={{ fontWeight: 700, fontSize: 12.5, color: '#334155' }}>Title / Name *</label>
            <Form.Control
              type="text"
              placeholder={holidayTypeInput === 'exam_day' ? 'e.g. Mid-Term Physics & Chemistry Exam' : 'e.g. Independence Day, Annual Sports Day'}
              value={holidayNameInput}
              onChange={e => this.setState({ holidayNameInput: e.target.value, holidayError: '' })}
              className="ttb-form-input"
              style={{ borderRadius: 8, height: 40 }}
            />
          </Form.Group>

          <Form.Group style={{ marginBottom: 14 }}>
            <label className="ttb-form-label" style={{ fontWeight: 700, fontSize: 12.5, color: '#334155' }}>Reason / Description (optional)</label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Additional details for staff and students..."
              value={holidayReasonInput}
              onChange={e => this.setState({ holidayReasonInput: e.target.value })}
              className="ttb-form-input"
              style={{ resize: 'none', borderRadius: 8 }}
            />
          </Form.Group>

          {/* Banner Live Preview */}
          {holidayNameInput.trim() && (
            <div style={{ background: selTypeObj.bg, border: `1.5px solid ${selTypeObj.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12 }}>
              <div style={{ color: selTypeObj.color, fontWeight: 800, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.05em' }}>
                <i className={selTypeObj.icon} style={{ marginRight: 4 }} /> Preview: {selTypeObj.label}
              </div>
              <div style={{ color: '#0f172a', fontWeight: 800, fontSize: 14, marginTop: 2 }}>
                {holidayNameInput}
              </div>
            </div>
          )}

          {holidayError && (
            <div className="ttb-modal-error" style={{ color: '#dc2626', background: '#fee2e2', border: '1px solid #fca5a5', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginTop: 8 }}>
              <i className="fal fa-exclamation-circle" style={{ marginRight: 6 }} />
              {holidayError}
            </div>
          )}

          <div className="ttb-modal-footer" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14, marginTop: 16 }}>
            <button type="button" className="ttb-btn outline" onClick={() => this.setState({ showHolidayModal: false })} disabled={holidayLoading}>
              Cancel
            </button>
            <button type="button" className="ttb-btn primary" onClick={this.saveHoliday} disabled={holidayLoading} style={{ background: 'linear-gradient(135deg, #ea580c, #c2410c)', borderColor: '#ea580c', padding: '8px 20px', borderRadius: 8, fontWeight: 700 }}>
              {holidayLoading ? <><i className="fal fa-spinner fa-spin mr-1" />Saving...</> : <><i className="fal fa-check mr-1" />Save Special Day</>}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  // ─── Main render ──────────────────────────────────────────────────────────
  render() {
    const { activeDay, saveFlash, selectedBranch, selectedClass, selectedSection,
            viewMode, branches } = this.state;
    const templates = Array.isArray(this.state.templates) ? this.state.templates : [];
    const templateCount = templates.length;

    // Show spinner while master data is loading
    if (!branches.length) {
      return (
        <div className="ttb-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
          <div style={{ textAlign: 'center', color: '#64748b' }}>
            <i className="fal fa-circle-notch fa-spin" style={{ fontSize: 32, marginBottom: 12 }} />
            <div style={{ fontSize: 14 }}>Loading timetable data…</div>
          </div>
        </div>
      );
    }

    const week    = this.getWeek();
    const daySch  = this.getDaySchedule(activeDay);
    const filled  = filledCount(daySch);
    const total   = weekTotal(week);

    return (
      <div className="ttb-wrap">

        <div className="ttb-page-header">
          <div>
            <h4 className="ttb-page-title">Timetable Builder</h4>
            <p className="ttb-page-sub">{selectedBranch} · {selectedClass} · Sec-{selectedSection} — 8 periods × 45 min</p>
          </div>
          <div className="ttb-header-actions">
            {/* Student / Staff view toggle */}
            <div className="ttb-view-toggle">
              <button className={viewMode === 'student' ? 'active' : ''}
                onClick={() => this.setState({ viewMode: 'student' })}>
                <i className="fal fa-user-graduate" /> Student View
              </button>
              <button className={viewMode === 'staff' ? 'active' : ''}
                onClick={() => this.setState({ viewMode: 'staff' })}>
                <i className="fal fa-chalkboard-teacher" /> Staff View
              </button>
            </div>
            <button className="ttb-btn ghost" title="Configure bell times" onClick={this.openBellConfig}>
              <i className="fal fa-bell" style={{ marginRight: 5 }} />Bell Times
            </button>
            <button className="ttb-btn ghost" title="Print timetable" onClick={this.handlePrint}>
              <i className="fal fa-print" style={{ marginRight: 5 }} />Print
            </button>
            <button className={`ttb-btn ${saveFlash ? 'success' : 'primary'} lg`} onClick={this.saveTimetable}>
              <i className={`fal fa-${saveFlash ? 'check' : 'save'}`} style={{ marginRight: 6 }} />
              {saveFlash ? 'Saved!' : 'Save Timetable'}
            </button>
          </div>
        </div>

        {this.renderContextBar()}

        {/* Week navigator */}
        {this.renderWeekNav()}

        {/* Day tabs with real dates + holiday indicator */}
        <div className="ttb-day-tabs">
          {[1,2,3,4,5,6].map(d => {
            const cnt     = filledCount(week[d]);
            const max     = PERIOD_SLOTS.length;
            const isHol   = this.isHoliday(d);
            const hol     = this.getHoliday(d);
            const hType   = hol ? HOLIDAY_TYPES.find(h => h.key === hol.type) : null;
            const dayDate = this.getDateForDay(d);
            const isToday = fmtDate(dayDate) === fmtDate(new Date());
            return (
              <button key={d}
                className={`ttb-day-tab ${activeDay === d ? 'active' : ''} ${isHol ? 'holiday' : ''} ${isToday ? 'today' : ''}`}
                onClick={() => this.setState({ activeDay: d })}>
                <span className="tab-day">
                  {DAYS_SHORT[d]}
                  {isToday && <span className="tab-today-dot" title="Today" />}
                </span>
                <span className="tab-date">{fmtShort(dayDate)}</span>
                {isHol ? (
                  <span className="tab-hol-dot" style={{ background: hType?.color || '#dc2626' }}>
                    <i className={hType?.icon || 'fal fa-moon'} />
                  </span>
                ) : (
                  <span className="tab-count" style={{ color: cnt === max ? '#3d8a55' : undefined }}>
                    {cnt}/{max}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="ttb-main-grid">
          <div>
            <div className="ttb-schedule-panel">
              <div className="ttb-schedule-head">
                <div>
                  <div className="ttb-day-label">
                    {DAYS[activeDay]}
                    <span className="ttb-day-date">{fmtFull(this.getDateForDay(activeDay))}</span>
                  </div>
                  <div className="ttb-head-meta">
                    {this.isHoliday(activeDay)
                      ? <span style={{ color: '#c0533a', fontWeight: 600 }}><i className="fal fa-moon" style={{ marginRight: 5 }} />Holiday declared — {this.getHoliday(activeDay)?.name}</span>
                      : viewMode === 'student'
                        ? `${filled}/${PERIOD_SLOTS.length} periods · ${filled * 45} min class time · ${PERIOD_SLOTS.length - filled} free`
                        : `Staff load — ${Object.keys((() => { const m = {}; PERIOD_SLOTS.forEach(s => { const e = daySch[s]; if (e?.teacherId) m[e.teacherId] = 1; }); return m; })()).length} lecturers active`
                    }
                  </div>
                </div>
                <div className="ttb-head-btns">
                  {this.isHoliday(activeDay) ? (
                    <button className="ttb-btn outline sm" onClick={() => this.openHolidayModal(activeDay)}>
                      <i className="fal fa-pen" /> Edit Holiday
                    </button>
                  ) : (
                    <button className="ttb-btn warning sm" onClick={() => this.openHolidayModal(activeDay)}>
                      <i className="fal fa-moon" /> Mark Holiday
                    </button>
                  )}
                  <button className="ttb-btn ghost sm" onClick={() => this.openCopyModal('day')}>
                    <i className="fal fa-copy" /> Copy Day
                  </button>
                </div>
              </div>

              {this.isOutOfTermRange(activeDay) && (
                <div style={{
                  background: 'linear-gradient(135deg, #fff7ed, #ffedd5)',
                  border: '1.5px solid #fed7aa',
                  borderRadius: 12,
                  padding: '14px 18px',
                  margin: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxShadow: '0 2px 8px rgba(249, 115, 22, 0.08)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ background: '#ea580c', color: '#ffffff', width: 36, height: 36, borderRadius: 10, display: 'grid', placeItems: 'center', fontSize: 18 }}>
                      <i className="fal fa-calendar-times" />
                    </span>
                    <div>
                      <div style={{ fontWeight: 800, color: '#c2410c', fontSize: 14 }}>
                        Date Out of Active Term Bounds ({fmtShort(this.getDateForDay(activeDay))})
                      </div>
                      <div style={{ fontSize: 12.5, color: '#9a3412', marginTop: 2 }}>
                        This date falls outside your configured timetable term range ({this.state.copyDateFrom || 'Start'} to {this.state.copyDateTo || 'End'}). Schedule periods are empty outside active term bounds.
                      </div>
                    </div>
                  </div>
                  <button
                    className="ttb-btn primary sm"
                    style={{ background: '#ea580c', borderColor: '#ea580c', fontWeight: 700, whiteSpace: 'nowrap' }}
                    onClick={() => this.openCopyModal('dateRange')}
                  >
                    <i className="fal fa-calendar-alt" style={{ marginRight: 6 }} />
                    Configure Term Dates
                  </button>
                </div>
              )}

              {viewMode === 'student' ? this.renderPeriodGrid() : this.renderStaffView()}
            </div>
          </div>

          <div className="ttb-side-panel">

            <div className="ttb-panel">
              <div className="ttb-panel-head">
                <span className="ttb-panel-title">Weekly Progress</span>
                <span style={{ fontSize: 11, color: '#a8a8b4', fontFamily: 'monospace' }}>{total}/48</span>
              </div>
              <div style={{ padding: '12px 16px' }}>{this.renderWeekFill()}</div>
            </div>

            <div className="ttb-panel">
              <div className="ttb-panel-head">
                <span className="ttb-panel-title">Teachers — {DAYS[activeDay]}</span>
              </div>
              <div style={{ padding: '8px 14px' }}>{this.renderTeacherLoad()}</div>
            </div>

            <div className="ttb-panel">
              <div className="ttb-panel-head"><span className="ttb-panel-title">Copy &amp; Replicate</span></div>
              <div className="ttb-copy-panel">
                {[
                  { mode: 'day',     cls: 'copy-day',     icon: 'fal fa-calendar-day', title: 'Copy Day → Other Days',   sub: 'Paste today to Mon, Tue…' },
                  { mode: 'section', cls: 'copy-section',  icon: 'fal fa-users',        title: 'Copy Week → Section',     sub: 'Apply full week to Sec-B, C' },
                ].map(({ mode, cls, icon, title, sub }) => (
                  <button key={mode} className={`copy-tool-btn ${cls}`} onClick={() => this.openCopyModal(mode)}>
                    <div className="ct-icon"><i className={icon} /></div>
                    <div className="ct-text"><div className="ct-title">{title}</div><div className="ct-sub">{sub}</div></div>
                  </button>
                ))}
                <button className="copy-tool-btn copy-template" onClick={() => this.setState({ showSaveTemplateModal: true })}>
                  <div className="ct-icon"><i className="fal fa-bookmark" /></div>
                  <div className="ct-text"><div className="ct-title">Save as Template</div><div className="ct-sub">Reuse this week for any class</div></div>
                </button>
                {templateCount > 0 && (
                  <button className="copy-tool-btn" style={{ borderColor: '#d5c7f0' }} onClick={() => this.setState({ showLoadTemplateModal: true })}>
                    <div className="ct-icon" style={{ background: '#f0ebfb', color: '#7c4dcc' }}><i className="fal fa-layer-group" /></div>
                    <div className="ct-text"><div className="ct-title">Apply Template</div><div className="ct-sub">{templateCount} saved</div></div>
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>

        {this.renderAssignModal()}
        {this.renderClashModal()}
        {this.renderHolidayModal()}
        {this.renderBellConfigModal()}
        {this.renderCopyModal()}
        {this.renderSaveTemplateModal()}
        {this.renderLoadTemplateModal()}
        {this.renderSubstituteModal()}
        {this.renderPrintView()}

        {/* Backdrop to close calendar picker */}
        {this.state.showCalendarPicker && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 99 }}
            onClick={() => this.setState({ showCalendarPicker: false })} />
        )}

        {/* Shown when a role without is_edit triggers any write (assign, clear, copy,
            template, subject, holiday, bell config, save). */}
        <AccessDeniedModal
          show={this.state.accessDeniedShow}
          onHide={this.guard.hideAccessDenied}
        />

        {/* Floating Toast Notification Banner */}
        {saveFlash && (
          <div style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: 'linear-gradient(135deg, #0f172a, #1e293b)',
            color: '#ffffff',
            padding: '12px 20px',
            borderRadius: 10,
            boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: 13,
            fontWeight: 700,
            border: '1px solid #334155'
          }}>
            <span style={{ background: '#22c55e', color: '#ffffff', width: 22, height: 22, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 11 }}>
              <i className="fal fa-check" />
            </span>
            {this.state.saveFlashMsg || 'Timetable Changes Saved Successfully!'}
          </div>
        )}
      </div>
    );
  }
}

export default withApollo(TimetableBuilder);
